import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ArrowRightLeft, Pointer, BadgeCheck, Delete, ArrowRight, ChevronLeft, QrCode,
  XCircle, CheckCircle2, Check, Clock, MapPin, User, CalendarPlus, CalendarDays, CalendarClock, Hash, ListChecks, Search,
  Users, Hourglass, UserPlus, List, RefreshCw, ClipboardList, AlertCircle, Loader2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Select } from 'antd';
import { useLocationData } from '../../hooks/useLocationData';
import { filterOptionVietnamese } from '../../lib/utils';
import { extractApiError, isFieldRequiredError } from '../../lib/apiError';
import * as api from './kioskApi';
import type { CheckinResult, CheckinTag, KioskResponse } from './kioskApi';
import QrScanner from './QrScanner';
import CountryCodePicker, { DEFAULT_DIAL, toStoredPhone } from './CountryCodePicker';
import DobPicker from './DobPicker';
import DatePadDialog from './DatePad';

/** Dịch vụ hiển thị ở màn đăng ký, dựng từ GET /services */
type KioskService = { id: number; name: string; categoryId: number | null; categoryName: string; price: number; priceText: string; search: string };

/** Khung giờ nhận khách (phút tính từ 0h), mỗi ô cách nhau 30 phút */
const SLOT_START = 8 * 60;
const SLOT_END = 21 * 60;
const SLOT_STEP = 30;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const toYmd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const toDm = (d: Date) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;

/** Các ô giờ của một ngày; hôm nay thì bỏ các giờ đã qua */
const timeSlotsFor = (day: Date) => {
  const now = new Date();
  const isToday = toYmd(day) === toYmd(now);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const slots: string[] = [];
  for (let m = SLOT_START; m <= SLOT_END; m += SLOT_STEP) {
    if (isToday && m <= nowMin) continue;
    slots.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  }
  return slots;
};

/** So 2 SĐT bỏ qua định dạng: "0909 506 018", "+84909506018", "84909506018" là một */
const nationalPhone = (p: string) => {
  const digits = String(p || '').replace(/\D/g, '').replace(/^0+/, '');
  return digits.startsWith('84') && digits.length === 11 ? digits.slice(2) : digits;
};
const samePhone = (a: string, b: string) => !!nationalPhone(a) && nationalPhone(a) === nationalPhone(b);

/** Bỏ dấu để khách gõ "nang co" vẫn ra "Nâng cơ" */
const foldVi = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

type DateMode = 'today' | 'tomorrow' | 'other';

/** Ngày ứng với lựa chọn Hôm nay / Ngày mai / Ngày khác */
const dayOf = (mode: DateMode, customDate: string) => {
  const today = startOfDay(new Date());
  if (mode === 'today') return today;
  if (mode === 'tomorrow') return addDays(today, 1);
  return customDate ? new Date(`${customDate}T00:00:00`) : addDays(today, 2);
};

/** `phone` là SĐT đã chuẩn hóa (toStoredPhone) hoặc lấy từ hồ sơ khách */
const freshRegData = (phone = '', name = '') => {
  // Hết giờ nhận khách hôm nay thì mặc định sang ngày mai
  const dateMode: DateMode = timeSlotsFor(new Date()).length ? 'today' : 'tomorrow';
  return {
    name,
    phone,
    /** Chỉ hỏi khi tạo khách mới: 1 nam, 2 nữ, 0 khác (theo API tạo khách hàng) */
    gender: null as number | null,
    dob: '',
    /** Khách tự chọn là người nước ngoài; null là chưa chọn, đoán theo hồ sơ / đầu số */
    isForeign: null as boolean | null,
    provinceCode: '',
    wardCode: '',
    address: '',
    selectedServices: [] as number[],
    dateMode,
    customDate: '',
    // Chọn sẵn ô giờ sớm nhất; giờ này trôi qua lúc khách còn điền form thì báo chọn lại, không tự đổi
    time: timeSlotsFor(dayOf(dateMode, ''))[0] || '',
  };
};

/** Kênh liên kết đầu tiên của khách, cách lấy giống trang Tạo lịch hẹn */
const channelConnectionOf = (profile: any): number | null => {
  const conn = (profile?.connections || []).find((c: any) => c?.channel_connection_id || c?.channel_connection?.id || c?.channel_customer?.channel_connection_id);
  const id = conn?.channel_connection_id ?? conn?.channel_connection?.id ?? conn?.channel_customer?.channel_connection_id;
  return id ? Number(id) : null;
};

/** Thông tin bù cho màn thành công khi lịch hẹn API trả về thiếu trường */
/** "1a, Phường Ô Môn, Thành phố Cần Thơ"; phần nào trống thì bỏ */
const joinAddress = (...parts: (string | null | undefined)[]) => parts.map(p => p?.trim()).filter(Boolean).join(', ');
const placeName = (x?: { name?: string; full_name?: string } | null) => x?.full_name || x?.name;
/** Lấy bản đầy đủ nhất giữa dữ liệu lịch hẹn trả về và địa chỉ khách vừa nhập */
const fullAddress = (appt: any, typed?: string) => {
  const c = appt?.customer;
  const fromAppt = joinAddress(appt?.address || c?.address, placeName(appt?.ward ?? c?.ward), placeName(appt?.province ?? c?.province));
  return (typed || '').length > fromAppt.length ? (typed as string) : fromAppt;
};

type SuccessFallback = { name?: string; phone?: string; address?: string; gender?: number | null; branch?: string; services?: any[]; price?: string };

/** Hộp thoại theo kết quả check-in */
type CheckinDialog =
  | { kind: 'suggest'; appointment: any }
  | { kind: 'create_appointment'; customer: { id: number; fullname?: string; display_name?: string; phone?: string }; expired: boolean }
  | { kind: 'create_customer'; phone: string };

/** Nhóm lịch hẹn trên màn Lễ tân, theo `customer_tag_status` của server */
type ReceptionBucket = 'upcoming' | 'waiting' | 'served' | 'canceled';
const RECEPTION_BUCKETS: ReceptionBucket[] = ['upcoming', 'waiting', 'served', 'canceled'];
/** 1 Đặt lịch mới, 2 Đã xác nhận → chưa đến; 3 Chờ thực hiện (đã check-in); 4 Đã thực hiện; 0 Hủy lịch */
const receptionBucket = (appt: any): ReceptionBucket => {
  const s = Number(appt?.customer_tag_status);
  if (s === 3) return 'waiting';
  if (s === 4) return 'served';
  if (s === 0) return 'canceled';
  return 'upcoming';
};
const RECEPTION_BADGE: Record<ReceptionBucket, string> = {
  upcoming: 'bg-blue-100 text-blue-700',
  waiting: 'bg-orange-100 text-orange-700',
  served: 'bg-green-100 text-green-700',
  canceled: 'bg-gray-100 text-gray-500',
};
/** Tự tải lại danh sách lịch hẹn khi đang mở màn Lễ tân */
const RECEPTION_REFRESH_MS = 60000;
/** Cơ sở đang xem trên màn Lễ tân, nhớ theo từng máy */
const RECEPTION_BRANCH_KEY = 'checkin_reception_branch';
/**
 * Cơ sở đặt máy kiosk, nhớ theo từng máy: lịch hẹn tạo ở kiosk luôn vào cơ sở này.
 * Cài một lần bằng cách mở /check-in?branch=<id cơ sở> (?branch=0 để xóa).
 */
const KIOSK_BRANCH_KEY = 'checkin_kiosk_branch';
const readKioskBranch = (): number | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('branch')) return Number(localStorage.getItem(KIOSK_BRANCH_KEY)) || null;
    const id = Number(params.get('branch')) || null;
    id ? localStorage.setItem(KIOSK_BRANCH_KEY, String(id)) : localStorage.removeItem(KIOSK_BRANCH_KEY);
    // Bỏ tham số khỏi thanh địa chỉ, tải lại trang vẫn dùng giá trị đã lưu
    params.delete('branch');
    const qs = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
    return id;
  } catch {
    return null;
  }
};
/** Nút nổi "Chuyển sang Lễ tân" trên màn khách; tạm ẩn, bật lại bằng `true` */
const SHOW_RECEPTION_SWITCH = false;

/** Kiosk đặt ở sảnh: khách bỏ dở thì sớm về màn chờ để khách sau không thấy thông tin của khách trước */
const IDLE_TIME = 90 * 1000;
/** Màn đăng ký / xác nhận có nhiều ô hơn nên cho khách thêm thời gian (chạm màn hình là tính lại) */
const IDLE_TIME_REGISTER = 3 * 60 * 1000;
/** Hỏi "Quý khách còn ở đây không?" trước khi tự về màn chờ */
const IDLE_WARN = 15 * 1000;
/** Màn thành công tự về màn chờ sau chừng này giây */
const SUCCESS_SECONDS = 15;
/** Trễ / sớm quá chừng này phút so với giờ hẹn thì báo cho nhân viên thấy */
const LATE_MIN = 15;
const EARLY_MIN = 30;

/**
 * Lượt check-in đang dở (màn, SĐT, form đăng ký, lịch chờ xác nhận) lưu theo tab trình duyệt,
 * lỡ tải lại trang thì khách tiếp tục ở đúng màn đang làm. Về màn chờ hoặc xong lượt thì xóa.
 */
const SESSION_KEY = 'checkin_session';
/** Màn còn dở sau khi tải lại; màn thành công tự về màn chờ nên không cần giữ */
const RESUMABLE_SCREENS = ['welcome', 'scanner', 'register', 'confirm', 'already'];

const readSession = () => {
  try {
    const s = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
    // Bỏ lâu hơn thời gian chờ thì đằng nào cũng đã về màn chờ
    if (!s || !RESUMABLE_SCREENS.includes(s.clientScreen) || Date.now() - s.savedAt > Math.max(IDLE_TIME, IDLE_TIME_REGISTER)) return null;
    return s;
  } catch {
    return null;
  }
};

const CustomStyles = () => (
  <style>{`
    .bg-spa-dark { background-color: #092C23; }
    .text-spa-dark { color: #092C23; }
    .bg-spa-gold { background-color: #C79F4B; }
    .text-spa-gold { color: #C79F4B; }
    .border-spa-gold { border-color: #C79F4B; }
    
    .fade-in { animation: fadeIn 0.4s ease-out forwards; }
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }

    @keyframes slideInRight {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
    }
    .animate-toast { animation: slideInRight 0.3s ease-out forwards; }

    @keyframes ripple {
        0% { transform: scale(1); opacity: 0.8; }
        100% { transform: scale(2.5); opacity: 0; }
    }
    .animate-ripple { animation: ripple 3s infinite; }
    .animate-ripple-delayed { animation: ripple 3s infinite 1.5s; }

    @keyframes subtleFloat {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-5px); }
    }
    .animate-float { animation: subtleFloat 4s ease-in-out infinite; }

    /* Ô chọn tỉnh / phường (antd) cùng cỡ chữ, bo góc, viền với các ô nhập khác của màn đăng ký */
    .kiosk-select.ant-select .ant-select-selector { border-radius: 0.5rem; border-color: #d1d5db; font-size: 14px; }
    .kiosk-select.ant-select-focused .ant-select-selector { border-color: #092C23 !important; box-shadow: none !important; }
    .kiosk-select.ant-select-status-error .ant-select-selector { border-color: #f87171 !important; }

    /* Màn thấp (iPad ngang 768px): thu gọn màn nhập SĐT để bàn số và nút quét QR nằm gọn, không phải cuộn */
    @media (max-height: 820px) {
      .kiosk-short-head { padding-top: 0.75rem !important; padding-bottom: 0.75rem !important; }
      .kiosk-short-hide { display: none !important; }
      .kiosk-short-pad { padding-top: 1.25rem !important; padding-bottom: 1.25rem !important; min-height: 0 !important; }
      .kiosk-short-mb { margin-bottom: 0.75rem !important; }
      .kiosk-short-key { height: 3.25rem !important; }
    }

    .no-scrollbar::-webkit-scrollbar { display: none; }
    .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

    ::-webkit-scrollbar { width: 6px; height: 6px; }
    ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
  `}</style>
);

const getCurrentTime = () => {
  const now = new Date();
  return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
};

/** 35000000 → "35.000.000đ" (cùng kiểu với các màn khác của CMS) */
const formatVnd = (n: number) => `${Math.round(n).toLocaleString('vi-VN')}đ`;

/** SĐT Việt Nam: số bàn (đầu 02) 10 số sau số 0 đầu, di động 9 số */
const vnNationalLength = (national: string) => (national.startsWith('2') ? 10 : 9);

/** "0901000777" → "0901 000 777" cho khách dễ đối chiếu */
const groupPhone = (p: string) => [p.slice(0, 4), p.slice(4, 7), p.slice(7)].filter(Boolean).join(' ');

/** Lệch so với giờ hẹn hôm nay (phút, dương là trễ); lịch ngày khác thì null */
const minutesLate = (scheduledAt?: string | null) => {
  if (!scheduledAt || isOtherDay(scheduledAt)) return null;
  const at = new Date(scheduledAt.replace(' ', 'T'));
  return Number.isNaN(at.getTime()) ? null : Math.round((Date.now() - at.getTime()) / 60000);
};

/** "2026-10-07 11:00:00" → "11:00"; hẹn ngày khác hôm nay thì kèm ngày "11:00 · 07/10" */
const scheduleLabel = (scheduledAt?: string | null) => {
  if (!scheduledAt) return '';
  const time = scheduledAt.slice(11, 16);
  const ymd = scheduledAt.slice(0, 10);
  if (ymd === toYmd(new Date())) return time;
  const [, month, day] = ymd.split('-');
  return `${time} · ${day}/${month}`;
};

/**
 * Kiosk đặt ở sảnh, ai nhập SĐT cũng xem được: che bớt thông tin khách trước khi hiển thị.
 * "0909123018" → "0909•••018"; "+12025550123" → "+120•••123"
 */
export const maskPhone = (p?: string | null) => {
  const s = String(p || '').replace(/[^\d+]/g, '');
  if (!s) return '';
  if (s.length <= 6) return `•••${s.slice(-2)}`;
  return `${s.slice(0, 4)}•••${s.slice(-3)}`;
};

/** "Nguyễn Văn Anh" → "N••• V•• Anh": giữ tên gọi để khách nhận ra mình, họ và tên đệm chỉ còn chữ đầu */
export const maskName = (name?: string | null) => {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) return words[0] || '';
  const masked = words.slice(0, -1).map(w => `${w[0]}${'•'.repeat(Math.min(Math.max(w.length - 1, 2), 3))}`);
  return [...masked, words[words.length - 1]].join(' ');
};

/** Lịch hẹn rơi vào ngày khác hôm nay (chưa tới hoặc đã qua); thiếu ngày hẹn thì không chặn */
const isOtherDay = (scheduledAt?: string | null) => !!scheduledAt && scheduledAt.slice(0, 10) !== toYmd(new Date());
/** Khách đến sớm tối đa bao nhiêu ngày thì kiosk tự chuyển lịch sang hôm nay; xa hơn (vd buổi liệu trình tuần sau) phải qua Lễ tân */
const EARLY_MAX_DAYS = 3;
/**
 * Lịch hẹn trong vài ngày tới: khách đến sớm vẫn check-in được (lịch chuyển sang hôm nay).
 * Lịch xa hơn hoặc ngày đã qua thì vẫn chặn — không để kiosk dời mất lịch hẹn khác của khách.
 */
const isFutureDay = (scheduledAt?: string | null) => {
  if (!scheduledAt) return false;
  const day = scheduledAt.slice(0, 10);
  return day > toYmd(new Date()) && day <= toYmd(new Date(Date.now() + EARLY_MAX_DAYS * 86_400_000));
};

/**
 * Lấy ID lịch hẹn từ nội dung QR. Mã in cho khách có dạng "code=337" (có thể nằm trong URL),
 * máy quét tay đôi khi chỉ trả về số trần.
 */
const appointmentIdFromQr = (text: string): string | null => {
  const m = text.match(/(?:^|[?&\s])code\s*=\s*(\d+)/i) || text.trim().match(/^(\d+)$/);
  return m ? m[1] : null;
};

/** Hỏi khách còn ở đây không, đếm ngược tới lúc tự về màn chờ */
const IdleWarning = () => {
  const { t } = useTranslation();
  const [left, setLeft] = useState(IDLE_WARN / 1000);
  useEffect(() => {
    const timer = setInterval(() => setLeft(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, []);
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center fade-in">
      <div className="absolute inset-0 bg-[#092C23]/70 backdrop-blur-sm" />
      <div className="relative bg-white rounded-[24px] shadow-2xl w-11/12 max-w-sm p-6 md:p-8 text-center">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border bg-amber-50 text-spa-gold border-amber-100">
          <Hourglass size={32} />
        </div>
        <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2">{t('checkin.idle_warn.title')}</h3>
        <p className="text-gray-500 text-sm md:text-base mb-6">{t('checkin.idle_warn.desc', { s: left })}</p>
        <button type="button" className="w-full py-3.5 rounded-xl bg-spa-dark text-white font-medium text-sm md:text-base shadow-md">
          {t('checkin.idle_warn.stay')}
        </button>
      </div>
    </div>
  );
};

// ĐÃ SỬA: Tách ClockDisplay ra ngoài Component chính
const ClockDisplay = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'vi-VN';
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const timerId = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timerId);
  }, []);
  return (
    <>
      <div className="text-5xl font-light tracking-widest text-white drop-shadow-md">
        {time.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false })}
      </div>
      <div className="text-xs uppercase tracking-[0.25em] mt-3 text-[#FDE08B] font-medium drop-shadow-sm">
        {time.toLocaleDateString(locale, { weekday: 'long', day: '2-digit', month: 'long' })}
      </div>
    </>
  );
};

const KIOSK_LANGUAGES = [
  { code: 'vi', label: 'VI', flag: '/images/flag/vi.png' },
  { code: 'en', label: 'EN', flag: '/images/flag/en.png' },
];

export default function KhothiCheckinSystem() {
  const { t, i18n } = useTranslation();
  const isVi = !i18n.language?.startsWith('en');
  const navigate = useNavigate();
  /**
   * Ngôn ngữ mặc định của kiosk là ngôn ngữ lúc mở trang. Khách đổi sang tiếng Anh thì
   * về màn chờ sẽ trả lại mặc định, để khách sau không bị kẹt ở ngôn ngữ của khách trước.
   */
  const [restored] = useState(readSession);
  const defaultLngRef = useRef<string>(restored?.defaultLng || i18n.language);

  /** Thông báo lỗi của server chỉ có tiếng Việt, sang tiếng Anh thì dùng câu dịch sẵn */
  const serverMsg = (msg: string | undefined, fallbackKey: string) => (isVi && msg ? msg : t(fallbackKey));

  const [currentView, setCurrentView] = useState('client');
  const [clientScreen, setClientScreen] = useState<string>(restored?.clientScreen || 'idle');
  /** Màn Lễ tân: lịch hẹn trong ngày lấy từ API */
  const [reception, setReception] = useState<{ status: 'idle' | 'loading' | 'ready' | 'error'; items: any[]; updatedAt: Date | null }>({ status: 'idle', items: [], updatedAt: null });
  const [receptionFilter, setReceptionFilter] = useState<ReceptionBucket | 'all'>('all');
  const [receptionSearch, setReceptionSearch] = useState('');
  const [receptionBranch, setReceptionBranch] = useState<number | null>(() => {
    try { return Number(localStorage.getItem(RECEPTION_BRANCH_KEY)) || null; } catch { return null; }
  });
  /** Lịch đang được bấm Check-in từ màn Lễ tân */
  const [receptionBusyId, setReceptionBusyId] = useState<number | null>(null);
  const [toasts, setToasts] = useState([]);
  const idleTimerRef = useRef(null);
  const warnTimerRef = useRef(null);
  /** Sắp tự về màn chờ: hiện lời hỏi khách còn ở đây không */
  const [idleWarn, setIdleWarn] = useState(false);
  /** Số giây còn lại trước khi màn thành công tự về màn chờ */
  const [successLeft, setSuccessLeft] = useState(SUCCESS_SECONDS);
  /** Ô còn thiếu của màn đăng ký, viền đỏ và cuộn tới ô đầu tiên */
  const [regErrors, setRegErrors] = useState<string[]>([]);

  // ĐÃ SỬA: Đưa các state của việc đăng ký ra Component ngoài cùng để không bị reset
  const [phone, setPhone] = useState<string>(restored?.phone || '');
  /** Đầu số quốc gia, không có dấu + */
  const [dialCode, setDialCode] = useState<string>(restored?.dialCode || DEFAULT_DIAL);
  const [dialog, setDialog] = useState<CheckinDialog | null>(null);
  const [successData, setSuccessData] = useState(null);
  const [regData, setRegData] = useState<ReturnType<typeof freshRegData>>(() => restored?.regData || freshRegData());
  /** Khách đã có hồ sơ (API trả create_appointment): thông tin lịch hẹn lấy từ hồ sơ gốc */
  const [regCustomer, setRegCustomer] = useState<{ id: number | null; profile: any | null; loading: boolean }>(
    () => restored?.regCustomer ? { ...restored.regCustomer, loading: false } : { id: null, profile: null, loading: false });
  const [branches, setBranches] = useState<{ id: number; name: string }[]>([]);
  const [kioskBranchId] = useState(readKioskBranch);
  /** Cơ sở của máy; chưa cài (hoặc id sai) thì mặc định cơ sở đầu tiên trong danh sách */
  const kioskBranch = branches.find(b => b.id === kioskBranchId) || branches[0] || null;
  /** Bước đang gửi của màn đăng ký; khác rỗng thì khóa nút và tạm dừng đếm thời gian chờ */
  const [submitStep, setSubmitStep] = useState<'' | 'customer' | 'booking' | 'checkin'>('');
  /**
   * Kết quả các bước đã xong của lần gửi đăng ký. Một bước sau lỗi thì bấm lại chỉ chạy tiếp,
   * không tạo khách / lịch hẹn lần nữa.
   */
  const submitProgress = useRef<{ customerId?: number; appointment?: { id: number; data: any } }>(restored?.submitProgress || {});
  const { provinceOptions, wardOptions, loadingWards, loadWards, setWardOptions } = useLocationData();
  /** Danh sách dịch vụ cho màn đăng ký, tải một lần khi mở màn lần đầu */
  const [kioskServices, setKioskServices] = useState<{ status: 'idle' | 'loading' | 'ready' | 'error'; items: KioskService[] }>({ status: 'idle', items: [] });
  const [serviceSearch, setServiceSearch] = useState<string>(restored?.serviceSearch || '');
  const [serviceCategory, setServiceCategory] = useState<number | 'all'>(restored?.serviceCategory ?? 'all');
  /** Bàn số chọn "Ngày khác" ở màn đăng ký */
  const [visitPadOpen, setVisitPadOpen] = useState(false);
  /** Đang tra cứu / gọi API check-in, khóa nút để không bấm trùng */
  const [checking, setChecking] = useState(false);
  /** Đổi key để mount lại máy quét sau một lần quét hỏng */
  const [scanKey, setScanKey] = useState(0);
  /**
   * Lịch hẹn đang hiển thị toàn màn hình:
   *  - màn 'confirm': chờ nhân viên xác nhận (API trả require_confirmation, server chưa đổi gì)
   *  - màn 'already': lịch đã check-in trước đó (already_in_progress), chỉ xem thông tin
   * `from` là màn quay về khi bấm Quay lại / Không đúng; `type` / `fallback` dùng cho màn thành công.
   */
  const [apptView, setApptView] = useState<{
    appointment: any; currentTag: CheckinTag | null; targetTag: CheckinTag | null;
    /** 'admin': mở từ nút Check-in của màn Lễ tân, xong thì quay về màn Lễ tân */
    from: 'welcome' | 'scanner' | 'register' | 'admin'; type: 'booked' | 'walkin'; fallback?: SuccessFallback;
  } | null>(restored?.apptView || null);

  // --- GIỮ LƯỢT CHECK-IN KHI TẢI LẠI TRANG ---
  useEffect(() => {
    if (!restored) return;
    if (restored.lng && i18n.language !== restored.lng) i18n.changeLanguage(restored.lng);
    // Danh sách phường / xã không lưu, tải lại theo tỉnh đã chọn
    if (restored.clientScreen === 'register' && restored.regData?.provinceCode) loadWards(restored.regData.provinceCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      if (currentView !== 'client' || !RESUMABLE_SCREENS.includes(clientScreen)) {
        sessionStorage.removeItem(SESSION_KEY);
        return;
      }
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        savedAt: Date.now(), clientScreen, phone, dialCode, regData, regCustomer, apptView, serviceSearch, serviceCategory,
        // Các bước đăng ký đã xong (khách / lịch hẹn đã tạo), để gửi lại không tạo trùng
        submitProgress: submitProgress.current,
        lng: i18n.language, defaultLng: defaultLngRef.current,
      }));
    } catch { /* trình duyệt chặn lưu trữ thì chỉ mất tính năng giữ lượt */ }
  }, [currentView, clientScreen, phone, dialCode, regData, regCustomer, apptView, serviceSearch, serviceCategory, submitStep, i18n.language]);

  // --- HỆ THỐNG TOAST MESSAGES ---
  const showToast = useCallback((message, type = 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);

  // --- QUẢN LÝ THỜI GIAN CHỜ (IDLE TIMER) ---
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    setIdleWarn(false);

    // Đang gửi đăng ký (nhiều API nối tiếp) thì không về màn chờ giữa chừng; màn thành công có đếm ngược riêng
    if (currentView === 'client' && clientScreen !== 'idle' && clientScreen !== 'success' && !submitStep) {
      const wait = clientScreen === 'register' || clientScreen === 'confirm' ? IDLE_TIME_REGISTER : IDLE_TIME;
      warnTimerRef.current = setTimeout(() => setIdleWarn(true), wait - IDLE_WARN);
      idleTimerRef.current = setTimeout(() => {
        setIdleWarn(false);
        setDialog(null);
        // Bỏ ngang màn xác nhận: server chưa đổi gì nên chỉ cần xóa lịch đang chờ
        setApptView(null);
        setPhone('');
        setClientScreen('idle');
      }, wait);
    }
  }, [currentView, clientScreen, submitStep]);

  useEffect(() => {
    const events = ['touchstart', 'mousemove', 'keypress', 'click', 'scroll'];
    const handleActivity = () => resetIdleTimer();
    events.forEach(e => window.addEventListener(e, handleActivity));
    return () => events.forEach(e => window.removeEventListener(e, handleActivity));
  }, [resetIdleTimer]);

  useEffect(() => { resetIdleTimer(); }, [clientScreen, currentView, resetIdleTimer]);

  // Màn thành công đếm ngược rồi tự về màn chờ, khách quên bấm "Về trang chủ" cũng không để lộ thông tin
  useEffect(() => {
    if (currentView !== 'client' || clientScreen !== 'success') return;
    const timer = setInterval(() => setSuccessLeft(s => Math.max(0, s - 1)), 1000);
    // Rời màn thành công thì nạp lại đủ giây ngay: để còn 0 thì lần thành công sau (của khách kế tiếp)
    // vừa hiện đã bị effect bên dưới đưa về màn chờ
    return () => { clearInterval(timer); setSuccessLeft(SUCCESS_SECONDS); };
  }, [currentView, clientScreen]);
  useEffect(() => {
    if (clientScreen !== 'success' || successLeft > 0) return;
    // Check-in xong (không phải chỉ đặt lịch): sang form khai báo thông tin ban đầu thay vì về màn chờ (họp 30/10)
    if (successData?.mode !== 'booked' && successData?.apptId) { openIntake(); return; }
    setPhone('');
    setClientScreen('idle');
  }, [clientScreen, successLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { document.title = 'Check-in | Khothi'; }, []);

  /** Form khai báo thông tin ban đầu (App Tư vấn) — tên, SĐT, địa chỉ chuyển qua state, không để lên URL */
  const openIntake = () => {
    const d = successData;
    setPhone('');
    setClientScreen('idle');
    navigate(`/khai-bao?appt=${d.apptId}`, { state: { name: d.name, phone: d.phone, address: d.address, gender: d.gender, branch: d.branch, back: '/check-in' } });
  };

  // Chỉ lấy dịch vụ đang hoạt động (status 0; 1 là đã khóa) và được gán hiển thị ở Check-in (display_area = checkin)
  const loadKioskServices = useCallback(async () => {
    setKioskServices(prev => ({ ...prev, status: 'loading' }));
    const res = await api.kioskServices();
    if (!res.status) {
      setKioskServices({ status: 'error', items: [] });
      return;
    }
    const items = (res.data?.items || res.data?.data || []).map((s: any) => {
      const discount = parseFloat(s.discount_price) || 0;
      const price = discount > 0 ? discount : parseFloat(s.price) || 0;
      const categoryName = s.category_name || s.category?.name || '';
      // Chỉ tìm theo tên + mã dịch vụ; lọc theo nhóm đã có nút riêng (tìm cả tên nhóm làm ra dịch vụ không chứa từ khoá)
      return { id: s.id, name: s.name, categoryId: s.category_id ?? null, categoryName, price, priceText: formatVnd(price), search: foldVi(`${s.name} ${s.code || ''}`) };
    });
    setKioskServices({ status: 'ready', items });
  }, []);

  useEffect(() => {
    if (clientScreen === 'register' && kioskServices.status === 'idle') loadKioskServices();
  }, [clientScreen, kioskServices.status, loadKioskServices]);

  useEffect(() => {
    // Tải ngay khi mở trang: màn chờ hiện tên cơ sở của máy, màn đăng ký đặt lịch vào cơ sở đó, màn Lễ tân lọc theo cơ sở
    if (branches.length) return;
    api.kioskBranches().then(res => {
      if (!res.status) return;
      const list = res.data?.items || res.data?.data || (Array.isArray(res.data) ? res.data : []);
      setBranches(list.map((b: any) => ({ id: b.id, name: b.name })));
    });
  }, [clientScreen, currentView, branches.length]);

  /** Tải lịch hẹn hôm nay cho màn Lễ tân; đang có dữ liệu thì tải ngầm, không xóa bảng */
  const loadReception = useCallback(async () => {
    setReception(prev => ({ ...prev, status: prev.items.length ? prev.status : 'loading' }));
    const res = await api.kioskDayAppointments(toYmd(new Date()), receptionBranch);
    if (!res.status) {
      setReception(prev => ({ ...prev, status: 'error' }));
      return;
    }
    const items = [...res.data].sort((a, b) => String(a.scheduled_at || '').localeCompare(String(b.scheduled_at || '')));
    setReception({ status: 'ready', items, updatedAt: new Date() });
  }, [receptionBranch]);

  useEffect(() => {
    if (currentView !== 'admin') return;
    loadReception();
    const timer = setInterval(loadReception, RECEPTION_REFRESH_MS);
    return () => clearInterval(timer);
  }, [currentView, loadReception]);

  const changeReceptionBranch = (id: number | null) => {
    try { id ? localStorage.setItem(RECEPTION_BRANCH_KEY, String(id)) : localStorage.removeItem(RECEPTION_BRANCH_KEY); } catch { /* chỉ là ghi nhớ trên máy */ }
    setReception({ status: 'loading', items: [], updatedAt: null });
    setReceptionBranch(id);
  };

  /**
   * Dựng dữ liệu màn thành công từ lịch hẹn API trả về (đã kèm dịch vụ, giá, tổng chi phí).
   * `fallback` dùng khi response thiếu trường, vd lịch vừa tạo chưa kèm giá dịch vụ.
   */
  const successFromAppointment = (appt: any, fallback: SuccessFallback = {}) => {
    const services = (appt?.services || []).map((s: any) => {
      const qty = Number(s.pivot?.quantity) || 1;
      // Giá lúc đặt lịch (pivot.price); thiếu thì dùng giá hiện tại của dịch vụ
      const unit = parseFloat(s.pivot?.price ?? s.price);
      const line = Number.isFinite(unit) ? { price: unit * qty, priceText: formatVnd(unit * qty) } : {};
      return { name: qty > 1 ? `${s.name} ×${qty}` : s.name, note: s.description || undefined, ...line };
    });
    const total = appt?.estimated_total_cost;
    const price = total !== undefined && total !== null
      ? (Number(total) === 0 ? t('checkin.free') : (appt.estimated_total_cost_formatted || formatVnd(Number(total))))
      : fallback.price;
    const useFallbackServices = !services.length || (fallback.services?.length && services.every((s: any) => s.priceText === undefined));
    const finalServices = useFallbackServices && fallback.services?.length ? fallback.services : services;
    return {
      apptId: appt?.id,
      name: appt?.customer_name || appt?.customer?.display_name || appt?.customer?.fullname || fallback.name || t('checkin.guest'),
      phone: appt?.customer_phone || appt?.customer?.phone || fallback.phone || '',
      address: fullAddress(appt, fallback.address),
      gender: appt?.customer_gender ?? appt?.customer?.gender ?? fallback.gender ?? undefined,
      code: appt?.code,
      scheduled: scheduleLabel(appt?.scheduled_at),
      scheduledAt: appt?.scheduled_at as string | undefined,
      services: finalServices,
      service: finalServices.map((s: any) => s.name).join(', '),
      price,
      branch: appt?.branch?.name || fallback.branch || '',
    };
  };

  // Về màn chờ thì trả ngôn ngữ mặc định; rời trang cũng trả lại để không ảnh hưởng phần CMS
  useEffect(() => {
    if (clientScreen !== 'idle') return;
    setDialCode(DEFAULT_DIAL);
    if (i18n.language !== defaultLngRef.current) i18n.changeLanguage(defaultLngRef.current);
  }, [clientScreen, i18n]);
  useEffect(() => () => {
    if (i18n.language !== defaultLngRef.current) i18n.changeLanguage(defaultLngRef.current);
  }, [i18n]);

  // --- HÀNH ĐỘNG CHECK-IN ---
  /** Báo check-in xong; màn Lễ tân tự tải lại danh sách từ API khi mở */
  const notifyCheckedIn = (name: string) => showToast(t('checkin.toast.customer_checked_in', { name }), 'success');

  /**
   * Bấm Check-in trên màn Lễ tân. Lịch cần xác nhận thì mở màn xác nhận (dùng chung với kiosk),
   * xác nhận hoặc bỏ qua xong sẽ quay về màn Lễ tân.
   */
  const checkinFromReception = async (appt: any) => {
    if (!appt?.id || receptionBusyId) return;
    setReceptionBusyId(appt.id);
    try {
      const res = await api.kioskCheckin({ id: appt.id });
      const data = res.data || {};
      if (!res.status) {
        showToast(res.authError ? t('checkin.toast.contact_reception') : serverMsg(res.msg, 'checkin.toast.checkin_failed'));
        return;
      }
      switch (data.action) {
        case 'require_confirmation':
          setDialog(null);
          setApptView({ appointment: data.appointment || appt, currentTag: data.current_tag ?? null, targetTag: data.target_tag ?? null, from: 'admin', type: 'booked' });
          setCurrentView('client');
          setClientScreen('confirm');
          return;
        case 'checkin_success':
          notifyCheckedIn(data.appointment?.customer_name || appt.customer_name || t('checkin.guest'));
          break;
        case 'already_in_progress':
          showToast(t('checkin.admin.already', { code: data.appointment?.code || appt.code || '' }), 'success');
          break;
        default:
          showToast(t('checkin.toast.checkin_failed'));
      }
      loadReception();
    } finally {
      setReceptionBusyId(null);
    }
  };

  /** Nút VI / EN; điện thoại chỉ hiện cờ cho gọn thanh tiêu đề */
  const renderLangSwitch = () => (
    <div className="flex items-center gap-1 p-1 rounded-full bg-[#092C23]/70 backdrop-blur-md border border-[#C79F4B]/40 shadow-lg">
      {KIOSK_LANGUAGES.map(lang => {
        const active = isVi ? lang.code === 'vi' : lang.code === 'en';
        return (
          <button
            key={lang.code}
            type="button"
            onClick={() => i18n.changeLanguage(lang.code)}
            aria-pressed={active}
            aria-label={lang.label}
            className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-full text-xs font-semibold tracking-wider transition-all ${
              active ? 'bg-[#C79F4B] text-white shadow' : 'text-white/80 hover:bg-white/10'
            }`}
          >
            <img src={lang.flag} alt="" className="w-5 h-3.5 object-cover rounded-[2px]" />
            <span className="hidden sm:inline">{lang.label}</span>
          </button>
        );
      })}
    </div>
  );

  // ĐÃ SỬA: Biến thành một hàm render thông thường thay vì một React Component lồng nhau
  const renderClientView = () => {
    // Numpad logic
    // Số nội địa Việt Nam: di động 10 số, số bàn (02x) 11 số, tính cả số 0 đầu; nước ngoài cho dài hơn
    const isVnPhone = dialCode === DEFAULT_DIAL;
    const maxDigits = isVnPhone ? vnNationalLength(phone.replace(/^0+/, '')) + (phone.startsWith('0') ? 1 : 0) : 13;
    const appendNumber = (num) => { if (phone.length < maxDigits) setPhone(p => p + num); };
    const deleteNumber = () => setPhone(p => p.slice(0, -1));

    /** Nhãn trễ / sớm so với giờ hẹn hôm nay, để nhân viên thấy ngay khi đối chiếu */
    const lateBadge = (scheduledAt?: string | null) => {
      const late = minutesLate(scheduledAt);
      if (late === null || (late <= LATE_MIN && late >= -EARLY_MIN)) return null;
      const abs = Math.abs(late);
      const [h, m] = [Math.floor(abs / 60), abs % 60];
      const time = !h ? t('checkin.duration.m', { m }) : !m ? t('checkin.duration.h', { h }) : t('checkin.duration.hm', { h, m });
      return late > 0
        ? <span className="px-2 py-0.5 rounded-full border border-red-100 bg-red-50 text-red-600 text-xs font-semibold whitespace-nowrap">{t('checkin.confirm.late', { time })}</span>
        : <span className="px-2 py-0.5 rounded-full border border-blue-100 bg-blue-50 text-blue-600 text-xs font-semibold whitespace-nowrap">{t('checkin.confirm.early', { time })}</span>;
    };

    /** Màn thành công (tự đếm ngược về màn chờ); `mode` 'booked' là đặt lịch ngày khác (hoặc chưa check-in được) */
    const finishCheckin = (data, mode: 'checkin' | 'booked' = 'checkin') => {
      setDialog(null);
      setSuccessData({ ...data, mode, time: getCurrentTime(), date: new Date() });
      setClientScreen('success');
    };

    /**
     * Xử lý kết quả API check-in theo `data.action` (dùng chung cho nhập SĐT và quét QR).
     * Trả về false khi không xử lý được để màn quét QR bật lại camera.
     */
    /** Mở màn xác nhận: nhân viên đọc lại thông tin với khách, đúng rồi mới check-in thật */
    const askConfirm = (data: CheckinResult, from: 'welcome' | 'scanner' | 'register', type: 'booked' | 'walkin' = 'booked', fallback?: SuccessFallback) => {
      setDialog(null);
      setApptView({ appointment: data.appointment, currentTag: data.current_tag ?? null, targetTag: data.target_tag ?? null, from, type, fallback });
      setClientScreen('confirm');
    };

    /** Lịch đã check-in trước đó: mở màn riêng hiển thị đầy đủ thông tin lịch hẹn */
    const showAlready = (appointment: any, from: 'welcome' | 'scanner' | 'register', fallback?: SuccessFallback) => {
      setDialog(null);
      setApptView({ appointment, currentTag: appointment?.customer_tag ?? null, targetTag: null, from, type: 'booked', fallback });
      setClientScreen('already');
    };

    const handleCheckinResult = (res: KioskResponse<CheckinResult>, ctx: { phone?: string; fromQr?: boolean } = {}) => {
      if (res.authError) {
        showToast(t('checkin.toast.contact_reception'));
        return false;
      }
      if (!res.status) {
        showToast(ctx.fromQr ? t('checkin.toast.qr_not_found') : serverMsg(res.msg, 'checkin.toast.checkin_failed'));
        return false;
      }
      const data = res.data || {};
      switch (data.action) {
        case 'checkin_success':
          finishCheckin({ ...successFromAppointment(data.appointment, { phone: ctx.phone }), type: 'booked', treatment: data.visit?.treatment });
          return true;
        case 'require_confirmation':
          askConfirm(data, ctx.fromQr ? 'scanner' : 'welcome', 'booked', { phone: ctx.phone });
          return true;
        case 'already_in_progress':
          showAlready(data.appointment, ctx.fromQr ? 'scanner' : 'welcome', { phone: ctx.phone });
          return true;
        case 'suggest_appointment':
          setDialog({ kind: 'suggest', appointment: data.suggested_appointment });
          return true;
        case 'create_appointment':
          setDialog({ kind: 'create_appointment', customer: data.customer, expired: !!data.expired_appointment });
          return true;
        case 'create_customer':
          setDialog({ kind: 'create_customer', phone: data.phone || ctx.phone || '' });
          return true;
        default:
          showToast(t('checkin.toast.checkin_failed'));
          return false;
      }
    };

    /**
     * API check-in chỉ dò SĐT trong hồ sơ khách, nhưng lịch hẹn có thể ghi SĐT khác hồ sơ
     * (người nhà đặt hộ, khách dùng số khác). API không ra hồ sơ khách thì tìm lịch hẹn ghi đúng SĐT này:
     * `pending` là lịch hôm nay chưa xong / chưa hủy, `latest` là lịch gần nhất để lấy hồ sơ khách.
     */
    const findAppointmentsByPhone = async (cleanPhone: string) => {
      const res = await api.kioskSearchAppointments(cleanPhone);
      if (!res.status) return null;
      const list = res.data?.items || res.data?.data || [];
      // `search` tìm gần đúng (cả tên, mã lịch), chỉ lấy lịch có SĐT trùng khớp
      const matches = list
        .filter((a: any) => a.customer_id && samePhone(a.customer_phone, cleanPhone))
        .sort((a: any, b: any) => String(b.scheduled_at || '').localeCompare(String(a.scheduled_at || '')));
      if (!matches.length) return null;
      const todayYmd = toYmd(new Date());
      const pending = [...matches].reverse().find((a: any) =>
        String(a.scheduled_at || '').startsWith(todayYmd) && receptionBucket(a) !== 'served' && receptionBucket(a) !== 'canceled');
      return { pending, latest: matches[0] };
    };

    // Submit logic: gửi SĐT, server tự chọn lịch hẹn phù hợp (ưu tiên hôm nay) rồi trả action
    const processCheckin = async (phoneToProcess = phone) => {
      const national = phoneToProcess.replace(/\D/g, '').replace(/^0+/, '');
      // Việt Nam: đúng 9 số (di động) / 10 số (số bàn) sau khi bỏ số 0 đầu; nước ngoài độ dài khác nhau nên chỉ chặn số quá ngắn
      if (dialCode === DEFAULT_DIAL ? national.length !== vnNationalLength(national) : national.length < 6) {
        showToast(t('checkin.toast.invalid_phone'));
        return;
      }
      const cleanPhone = toStoredPhone(dialCode, national);
      if (checking) return;

      setChecking(true);
      try {
        let res = await api.kioskCheckin({ customer_phone: cleanPhone });
        const action = res.data?.action;
        // SĐT chỉ có trên lịch hẹn: API trả create_customer, hoặc create_appointment kèm tên lấy từ lịch nhưng `customer.id` rỗng
        if (res.status && (action === 'create_customer' || (action === 'create_appointment' && !res.data?.customer?.id))) {
          const linked = await findAppointmentsByPhone(cleanPhone);
          if (linked?.pending) {
            // Lịch hôm nay ghi SĐT này: check-in theo lịch như quét QR
            res = await api.kioskCheckin({ id: linked.pending.id });
          } else if (linked) {
            const a = linked.latest;
            res = { ...res, data: { action: 'create_appointment', customer: {
              id: Number(a.customer_id), fullname: res.data?.customer?.fullname || a.customer?.fullname || a.customer_name, display_name: a.customer?.display_name, phone: cleanPhone,
            } } };
          }
        }
        handleCheckinResult(res, { phone: cleanPhone });
      } finally {
        setChecking(false);
      }
    };

    const handleQrResult = async (text: string) => {
      const id = appointmentIdFromQr(text);
      if (!id) {
        showToast(t('checkin.toast.qr_invalid'));
        setScanKey(k => k + 1);
        return;
      }
      setChecking(true);
      try {
        const ok = handleCheckinResult(await api.kioskCheckin({ id }), { fromQr: true });
        if (!ok) setScanKey(k => k + 1);
      } finally {
        setChecking(false);
      }
    };

    /** Khách đồng ý chuyển sang lịch hẹn mới mà API gợi ý (QR của lịch đã kết thúc / hủy) */
    const checkinSuggested = async (appointmentId: number) => {
      setDialog(null);
      setChecking(true);
      try {
        handleCheckinResult(await api.kioskCheckin({ id: appointmentId }), { fromQr: true });
      } finally {
        setChecking(false);
      }
    };

    /**
     * Nhân viên đã đối chiếu thông tin với khách: check-in thật. Gửi lại đúng `id` của lịch đang hiển thị
     * (không gửi SĐT để server không dò ra lịch khác) kèm `confirm: true`.
     */
    /** Mở từ màn Lễ tân: xong việc thì quay lại màn Lễ tân và tải lại danh sách */
    const backToReception = () => {
      setApptView(null);
      setClientScreen('idle');
      setCurrentView('admin');
      loadReception();
    };

    const confirmPendingCheckin = async () => {
      const p = apptView;
      if (!p?.appointment?.id || checking) return;
      const at = p.appointment.scheduled_at;
      // Lịch ngày đã qua: không check-in (sẽ chuyển thẻ S ngay hôm nay), màn xác nhận đã ẩn nút này
      if (isOtherDay(at) && !isFutureDay(at)) return;
      setChecking(true);
      try {
        // Khách đến sớm hơn ngày hẹn: chuyển lịch sang giờ hiện tại hôm nay trước, để Lễ tân / báo cáo trong ngày thấy khách
        if (isFutureDay(at)) {
          const now = new Date();
          const moved = await api.kioskReschedule(p.appointment.id, {
            customer_id: p.appointment.customer_id ?? undefined,
            scheduled_at: `${toYmd(now)} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:00`,
            ...(p.appointment.branch_id ? { branch_id: p.appointment.branch_id } : {}),
          });
          if (!moved.status) {
            showToast(moved.authError ? t('checkin.toast.contact_reception') : t('checkin.toast.reschedule_failed'));
            return;
          }
        }
        const res = await api.kioskCheckin({ id: p.appointment.id, confirm: true });
        if (res.status && res.data?.action === 'checkin_success') {
          if (p.from === 'admin') {
            notifyCheckedIn(res.data.appointment?.customer_name || p.appointment.customer_name || t('checkin.guest'));
            backToReception();
            return;
          }
          setApptView(null);
          finishCheckin({ ...successFromAppointment(res.data.appointment || p.appointment, p.fallback), type: p.type, treatment: res.data.visit?.treatment });
          return;
        }
        if (res.status && res.data?.action === 'already_in_progress' && p.from === 'admin') {
          showToast(t('checkin.admin.already', { code: res.data.appointment?.code || p.appointment.code || '' }), 'success');
          backToReception();
          return;
        }
        if (res.status && res.data?.action === 'already_in_progress') {
          // Có người check-in lịch này trước đó trong lúc đang xác nhận
          showAlready(res.data.appointment || p.appointment, p.from === 'register' || p.from === 'admin' ? 'welcome' : p.from, p.fallback);
          return;
        }
        // Lỗi thì ở lại màn xác nhận để thử lại
        showToast(res.authError ? t('checkin.toast.contact_reception') : serverMsg(res.msg, 'checkin.toast.checkin_failed'));
      } finally {
        setChecking(false);
      }
    };

    /** Thông tin không đúng: chưa check-in, quay lại màn trước để tra cứu lại */
    const rejectPendingCheckin = () => {
      const p = apptView;
      const from = p?.from || 'welcome';
      if (from === 'admin') {
        backToReception();
        return;
      }
      setApptView(null);
      // Khách vãng lai: lịch đã tạo xong, quay lại form sẽ tạo trùng — giữ lịch đã đặt, chưa check-in
      if (from === 'register' && p) {
        finishCheckin({ ...successFromAppointment(p.appointment, p.fallback), type: p.type }, 'booked');
        showToast(t('checkin.toast.checkin_pending'));
        return;
      }
      if (from === 'scanner') setScanKey(k => k + 1);
      setClientScreen(from);
    };

    /** Đóng hộp thoại; đang ở màn quét QR thì bật lại camera để quét mã khác */
    const closeDialog = () => {
      setDialog(null);
      if (clientScreen === 'scanner') setScanKey(k => k + 1);
    };

    /** Nội dung hộp thoại theo từng kết quả check-in; `cancel` null là hộp thoại chỉ có một nút */
    const dialogView = (d: CheckinDialog) => {
      const amber = 'bg-amber-50 text-spa-gold border-amber-100';
      const typedPhone = phone ? toStoredPhone(dialCode, phone) : '';
      switch (d.kind) {
        case 'suggest':
          return {
            icon: <CalendarClock size={36} />, tone: amber,
            title: t('checkin.dialog.suggest_title'),
            desc: t('checkin.dialog.suggest_desc', { code: d.appointment?.code || '', time: scheduleLabel(d.appointment?.scheduled_at) }),
            confirm: t('checkin.dialog.suggest_confirm'),
            onConfirm: () => checkinSuggested(d.appointment?.id),
            cancel: t('checkin.modal.cancel'),
          };
        case 'create_appointment': {
          const name = d.customer?.fullname || d.customer?.display_name || '';
          return {
            icon: <CalendarPlus size={36} />, tone: amber,
            title: t('checkin.modal.title'),
            desc: d.expired ? t('checkin.dialog.qr_expired_desc') : t('checkin.dialog.no_appointment_desc', { name: maskName(name) }),
            confirm: t('checkin.modal.create'),
            onConfirm: () => openRegister(d.customer.id, d.customer.phone || typedPhone, name),
            cancel: t('checkin.modal.cancel'),
          };
        }
        case 'create_customer':
          return {
            icon: <UserPlus size={36} />, tone: amber,
            title: t('checkin.dialog.new_customer_title'),
            // Nhắc lại số vừa nhập để khách nhận ra mình gõ nhầm
            desc: t('checkin.dialog.new_customer_desc', { phone: !phone ? d.phone : isVnPhone ? groupPhone(phone) : `+${dialCode} ${phone}` }),
            confirm: t('checkin.dialog.new_customer_confirm'),
            onConfirm: () => openRegister(null, d.phone || typedPhone),
            cancel: t('checkin.modal.cancel'),
          };
      }
    };

    /** Màn trước đó của từng màn; về màn chờ thì xóa luôn SĐT đã nhập */
    const goBack = () => {
      if (clientScreen === 'confirm') {
        rejectPendingCheckin();
        return;
      }
      if (clientScreen === 'already') {
        const from = apptView?.from || 'welcome';
        if (from === 'admin') {
          backToReception();
          return;
        }
        setApptView(null);
        if (from === 'scanner') setScanKey(k => k + 1);
        setClientScreen(from);
        return;
      }
      if (clientScreen === 'scanner' || clientScreen === 'register') {
        setClientScreen('welcome');
        return;
      }
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      setDialog(null);
      setPhone('');
      setClientScreen('idle');
    };

    /**
     * Mở màn đăng ký. Khách đã có hồ sơ (`customerId`) thì điền sẵn và khóa họ tên / SĐT theo hồ sơ gốc,
     * chỉ cho bổ sung địa chỉ; khách mới thì nhập đầy đủ.
     */
    const openRegister = async (customerId: number | null, regPhone: string, name = '') => {
      setDialog(null);
      submitProgress.current = {};
      setRegErrors([]);
      setRegData(freshRegData(regPhone, name));
      setWardOptions([]);
      setServiceSearch('');
      setServiceCategory('all');
      setRegCustomer({ id: customerId, profile: null, loading: !!customerId });
      setClientScreen('register');
      if (!customerId) return;

      const res = await api.kioskCustomerDetail(customerId);
      const profile = res.status ? res.data : null;
      setRegCustomer({ id: customerId, profile, loading: false });
      if (!profile) return;
      // Không điền sẵn địa chỉ trong hồ sơ: ai nhập SĐT này cũng sẽ đọc được. Để trống thì giữ địa chỉ cũ
      setRegData(prev => ({
        ...prev,
        name: profile.display_name || profile.fullname || prev.name,
        // Giữ SĐT khách vừa nhập: có thể khác SĐT hồ sơ khi tìm ra khách qua lịch hẹn cũ
        phone: prev.phone || profile.phone,
      }));
    };

    // --- Ngày, giờ & dịch vụ của màn đăng ký ---
    const today = startOfDay(new Date());
    const regDay = dayOf(regData.dateMode, regData.customDate);
    const regSlots = timeSlotsFor(regDay);
    const regIsToday = toYmd(regDay) === toYmd(today);
    /** Đổi ngày thì chọn sẵn ô giờ sớm nhất của ngày mới */
    const setRegDate = (dateMode: DateMode, customDate = regData.customDate) =>
      setRegData(prev => ({ ...prev, dateMode, customDate, time: timeSlotsFor(dayOf(dateMode, customDate))[0] || '' }));

    /**
     * "Ngày khác": khách chỉ gõ ngày + tháng (4 chữ số), năm tự lấy năm nay,
     * ngày đó đã qua thì sang năm sau. Trả về "YYYY-MM-DD" hoặc null nếu không có ngày đó.
     */
    const parseVisitDate = (digits: string) => {
      if (digits.length !== 4) return null;
      const day = Number(digits.slice(0, 2));
      const month = Number(digits.slice(2));
      for (const year of [today.getFullYear(), today.getFullYear() + 1]) {
        const d = new Date(year, month - 1, day);
        if (d.getMonth() === month - 1 && d.getDate() === day && d >= today) return toYmd(d);
      }
      return null;
    };
    const pickVisitDate = (ymd: string) => {
      setVisitPadOpen(false);
      // Trùng hôm nay / ngày mai thì sáng nút tương ứng cho khách dễ nhận ra
      if (ymd === toYmd(today)) setRegDate('today');
      else if (ymd === toYmd(addDays(today, 1))) setRegDate('tomorrow');
      else setRegDate('other', ymd);
    };

    const serviceCategories = (() => {
      const seen = new Map<number, string>();
      kioskServices.items.forEach(s => { if (s.categoryId != null && !seen.has(s.categoryId)) seen.set(s.categoryId, s.categoryName); });
      return [...seen].map(([id, name]) => ({ id, name }));
    })();
    const searchKey = foldVi(serviceSearch.trim());
    const visibleServices = kioskServices.items.filter(s =>
      (serviceCategory === 'all' || s.categoryId === serviceCategory) && (!searchKey || s.search.includes(searchKey)));
    const selectedDocs = kioskServices.items.filter(s => regData.selectedServices.includes(s.id));
    const selectedTotal = selectedDocs.reduce((sum, s) => sum + s.price, 0);

    // --- Khách & địa chỉ của màn đăng ký ---
    const regProfile = regCustomer.profile;
    /** Họ tên / SĐT lấy từ hồ sơ gốc thì không cho sửa ở đây */
    const regLocked = !!(regCustomer.id && regProfile);
    // Khách nước ngoài chỉ cần địa chỉ chi tiết; chưa tự chọn thì theo hồ sơ, khách mới thì đoán theo đầu số đã nhập
    const profileForeign = Number(regProfile?.is_foreign) === 1;
    const regForeign = regData.isForeign ?? (regProfile ? profileForeign : (regData.phone ? dialCode !== DEFAULT_DIAL : false));
    /** Hồ sơ đã đủ địa chỉ (ẩn trên kiosk): khách để trống các ô địa chỉ thì giữ nguyên địa chỉ hồ sơ */
    const profileAddressOk = !!(regProfile?.address && (regForeign || (regProfile.province_code && regProfile.ward_code)));
    const keepProfileAddress = profileAddressOk && !regData.provinceCode && !regData.wardCode && !regData.address.trim();
    /** Ô đã bị báo thiếu mà khách chưa điền lại: viền đỏ, điền xong thì tự hết */
    const regMissing = {
      name: !regData.name.trim(), province: !regData.provinceCode, ward: !regData.wardCode,
      address: !regData.address.trim(), time: !regData.time, services: !regData.selectedServices.length,
    };
    const hasErr = (f: keyof typeof regMissing) => regErrors.includes(f) && regMissing[f];
    const errText = (f: keyof typeof regMissing, key: string) => hasErr(f) && <p className="text-xs text-red-500 mt-1">{t(key)}</p>;
    const handleProvinceChange = (code?: string) => {
      setRegData(prev => ({ ...prev, provinceCode: code || '', wardCode: '' }));
      setWardOptions([]);
      if (code) loadWards(code);
    };

    /** Ô còn thiếu: viền đỏ, cuộn tới ô đầu tiên (có thể đã trôi khỏi màn hình) và báo lý do */
    const invalid = (fields: string[], msg: string) => {
      setRegErrors(fields);
      document.getElementById(`reg-${fields[0]}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      showToast(msg);
    };

    const submitRegistration = async () => {
      if (submitStep) return;
      const name = regData.name.trim();
      if (!name) return invalid(['name'], t('checkin.toast.name_required'));
      // Kiosk không hỏi chi nhánh: đặt vào cơ sở đã cài cho máy (chưa cài thì cơ sở đầu tiên); chưa tải được danh sách thì báo lỗi
      const branch = kioskBranch;
      if (!branch) return showToast(t('checkin.toast.branch_required'));
      const missing = keepProfileAddress ? [] : ([
        !regForeign && !regData.provinceCode && 'province',
        !regForeign && !regData.wardCode && 'ward',
        !regData.address.trim() && 'address',
      ].filter(Boolean) as string[]);
      if (missing.length) {
        return invalid(missing, t('checkin.toast.address_required', { fields: missing.map(f => t(`checkin.register.${f}`)).join(', ') }));
      }
      if (!regData.time) return invalid(['time'], t('checkin.toast.time_required'));
      // Giờ đã chọn trôi qua trong lúc khách điền form: báo chọn lại, không tự đổi giờ
      if (!regSlots.includes(regData.time)) {
        setRegData(prev => ({ ...prev, time: '' }));
        return invalid(['time'], t('checkin.toast.time_expired'));
      }
      if (!regData.selectedServices.length) return invalid(['services'], t('checkin.toast.service_required'));
      setRegErrors([]);

      const fail = (res: KioskResponse, fallbackKey: string) => {
        if (res.authError) return showToast(t('checkin.toast.contact_reception'));
        if (isFieldRequiredError(res.raw, 'address')) {
          return showToast(t('checkin.toast.address_required', { fields: t('checkin.register.address') }));
        }
        // Lỗi của API chỉ có tiếng Việt, sang tiếng Anh thì dùng câu dịch sẵn
        showToast((isVi && extractApiError(res.raw, 'vi')) || t(fallbackKey));
      };

      const progress = submitProgress.current;
      const location = keepProfileAddress ? {
        province_code: regProfile.province_code || undefined,
        ward_code: regProfile.ward_code || undefined,
        address: regProfile.address || '',
      } : {
        province_code: regData.provinceCode || undefined,
        ward_code: regData.wardCode || undefined,
        address: regData.address.trim(),
      };
      const branchName = branch.name || '';
      const fallback = {
        name,
        phone: regData.phone,
        // Khai báo y tế cần địa chỉ đủ phường, tỉnh chứ không chỉ số nhà
        address: joinAddress(
          location.address,
          keepProfileAddress ? regProfile?.ward?.name : wardOptions.find(o => o.value === regData.wardCode)?.label,
          keepProfileAddress ? regProfile?.province?.name : provinceOptions.find(o => o.value === regData.provinceCode)?.label,
        ),
        gender: regData.gender ?? regProfile?.gender,
        branch: branchName,
        services: selectedDocs.map(s => ({ name: s.name, note: s.categoryName, price: s.price, priceText: s.priceText })),
        price: selectedTotal === 0 ? t('checkin.free') : formatVnd(selectedTotal),
      };

      try {
        // 1. Hồ sơ khách là nguồn gốc của thông tin trên lịch hẹn: khách mới thì tạo,
        //    khách cũ đổi / bổ sung địa chỉ thì lưu vào hồ sơ trước
        setSubmitStep('customer');
        let customerId = progress.customerId ?? regCustomer.id ?? undefined;
        /**
         * Khách mới hoàn toàn (chưa có hồ sơ): gửi is_auto_staff = 1 khi tạo khách và lịch hẹn để API tự gán
         * Trực page / Telesale theo cài đặt appointment_default_staff. Khách cũ giữ nhân viên trong hồ sơ.
         */
        const isNewCustomer = !regCustomer.id;
        if (!customerId) {
          const res = await api.kioskCreateCustomer({
            display_name: name,
            fullname: name,
            phone: regData.phone,
            gender: regData.gender ?? undefined,
            birthday: regData.dob || undefined,
            is_foreign: regForeign ? 1 : 0,
            branch_id: branch.id,
            is_auto_staff: 1,
            ...location,
          });
          customerId = Number(res.data?.id) || undefined;
          if (!res.status || !customerId) return fail(res, 'checkin.toast.customer_failed');
          progress.customerId = customerId;
        } else if (
          !progress.customerId && (
            (regProfile?.address || '') !== location.address ||
            (regProfile?.province_code || '') !== (location.province_code || '') ||
            (regProfile?.ward_code || '') !== (location.ward_code || '') ||
            profileForeign !== regForeign)
        ) {
          const res = await api.kioskUpdateCustomer(customerId, { ...location, is_foreign: regForeign ? 1 : 0 });
          if (!res.status) return fail(res, 'checkin.toast.profile_failed');
          progress.customerId = customerId;
        }

        // 2. Tạo lịch hẹn, payload giống trang Tạo lịch hẹn; thông tin khách đọc lại từ hồ sơ mới nhất
        if (!progress.appointment) {
          setSubmitStep('booking');
          const fresh = await api.kioskCustomerDetail(customerId);
          const p = fresh.status && fresh.data ? fresh.data : {};
          const pick = (...vals: any[]) => vals.find(v => v !== undefined && v !== null && v !== '');
          const payload: Record<string, any> = {
            customer_id: customerId,
            customer_name: pick(p.display_name, name),
            branch_id: branch.id,
            status: 1,
            // Theo lựa chọn trên form (đã lưu vào hồ sơ ở bước 1)
            is_foreign: regForeign ? 1 : 0,
            scheduled_at: `${toYmd(regDay)} ${regData.time}:00`,
            service_ids: regData.selectedServices,
          };
          const optional = {
            // SĐT nhập ở kiosk, như các lịch cũ của khách; trùng SĐT hồ sơ trong trường hợp thường
            customer_phone: pick(regData.phone, p.phone),
            customer_email: p.email,
            customer_gender: p.gender,
            customer_dob: p.birthday,
            province_code: pick(p.province_code, location.province_code),
            ward_code: pick(p.ward_code, location.ward_code),
            address: pick(p.address, location.address),
            channel_connection_id: channelConnectionOf(p),
            // Khách cũ: nhân viên phụ trách theo hồ sơ; khách mới để API tự gán (is_auto_staff bên dưới)
            ...(isNewCustomer ? {} : { staff_id: p.staff_id, telesale_id: p.telesale_id }),
            is_auto_staff: isNewCustomer ? 1 : undefined,
          };
          Object.entries(optional).forEach(([key, value]) => { if (value) payload[key] = value; });

          const res = await api.kioskCreateAppointment(payload);
          const created = res.data?.appointment || res.data;
          const appointmentId = Number(res.data?.id ?? res.data?.appointment_id ?? res.data?.appointment?.id) || 0;
          if (!res.status || !appointmentId) return fail(res, 'checkin.toast.booking_failed');
          progress.appointment = { id: appointmentId, data: created };
        }

        const appointment = progress.appointment;
        // 3. Hẹn ngày khác chỉ đặt lịch, không check-in
        if (!regIsToday) {
          submitProgress.current = {};
          finishCheckin({ ...successFromAppointment(appointment.data, fallback), type: 'walkin' }, 'booked');
          return;
        }

        setSubmitStep('checkin');
        const res = await api.kioskCheckin({ id: appointment.id });
        submitProgress.current = {};
        const action = res.data?.action;
        if (res.status && (action === 'checkin_success' || action === 'already_in_progress')) {
          finishCheckin({ ...successFromAppointment(res.data.appointment, fallback), type: 'walkin', treatment: res.data.visit?.treatment });
          return;
        }
        if (res.status && action === 'require_confirmation') {
          askConfirm({ ...res.data, appointment: res.data.appointment || { ...appointment.data, id: appointment.id } }, 'register', 'walkin', fallback);
          return;
        }
        // Lịch đã tạo nhưng check-in chưa được: không tạo lịch lần nữa, nhờ khách báo lễ tân
        finishCheckin({ ...successFromAppointment(appointment.data, fallback), type: 'walkin' }, 'booked');
        showToast(t('checkin.toast.checkin_pending'));
      } finally {
        setSubmitStep('');
      }
    };

    /**
     * Màn chờ và màn nhập SĐT là thẻ nổi giữa màn hình; các màn còn lại phủ kín màn hình,
     * header và chân màn đứng yên, chỉ nội dung ở giữa cuộn (vừa khung iPad dọc lẫn ngang).
     */
    const fullScreen = clientScreen !== 'idle' && clientScreen !== 'welcome';

    return (
      <div className={`flex items-center justify-center relative w-full bg-[url('https://vienthammykhothi.vn/uploads/2025/11/photo_library/C4xgoSiQ876b912cca89206e784b255cad32861d7e.jpg')] bg-cover bg-center ${fullScreen ? 'flex-none h-[100dvh] overflow-hidden' : 'flex-1 min-h-screen p-4'}`}>
        <div className="absolute inset-0 bg-[#092C23]/40 backdrop-blur-[2px]"></div>

        {/* Điện thoại giữ khung hẹp; iPad dọc (≥768px) và ngang (≥1024px) nới rộng cho dễ thao tác */}
        <div className={`relative z-10 w-full overflow-hidden fade-in ${fullScreen ? 'h-full flex flex-col' : 'max-w-md md:max-w-2xl lg:max-w-3xl rounded-2xl shadow-2xl'}`}>
          {clientScreen !== 'idle' && (
            <div className={`bg-spa-dark px-6 py-6 text-center relative overflow-hidden flex flex-col items-center justify-center shrink-0 ${clientScreen === 'welcome' ? 'kiosk-short-head' : ''}`}>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#C79F4B] via-[#FDE08B] to-[#C79F4B]"></div>
              {/*
                Nút quay lại chỉ ở màn nhập SĐT; các màn sau đã có nút Quay lại / Không đúng / Huỷ ở chân màn,
                màn thành công thì không quay lại được nữa.
                Căn bằng inset-y + flex, không dùng translate vì cộng dồn với active:scale
              */}
              {clientScreen === 'welcome' && (
                <div className="absolute inset-y-0 left-4 flex items-center">
                  <button
                    type="button"
                    onClick={goBack}
                    disabled={checking || !!submitStep}
                    aria-label={t('checkin.nav.back')}
                    className="flex items-center gap-1.5 h-10 pl-2 pr-3.5 rounded-full border border-[#C79F4B]/40 bg-white/5 text-[#FDE08B] text-sm font-medium
                               hover:bg-[#C79F4B]/20 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {/* Chữ thường có tâm thấp hơn tâm dòng ~2px, hạ icon theo cho cân mắt */}
                    <ChevronLeft size={18} strokeWidth={2.25} className="relative top-[1.5px] shrink-0" />
                    <span className="hidden sm:inline leading-5">{t('checkin.nav.back')}</span>
                  </button>
                </div>
              )}
              <img src="https://khothi-cms.buso.asia/images/logo-v.png" alt="Khothi" className="h-14 object-contain drop-shadow-md rounded-[5px]" />
              {/* Chuyển ngôn ngữ nằm trong thanh tiêu đề, không đè lên mép thẻ */}
              <div className="absolute inset-y-0 right-4 flex items-center">{renderLangSwitch()}</div>
            </div>
          )}

          {/* MÀN HÌNH: CHỜ (IDLE) */}
          {clientScreen === 'idle' && (
            <div className="bg-[#092C23]/80 backdrop-blur-md p-8 md:p-12 flex flex-col items-center justify-between cursor-pointer min-h-[550px] relative overflow-hidden" 
                 onClick={() => { setPhone(''); setClientScreen('welcome'); }}>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#C79F4B]/10 via-transparent to-transparent pointer-events-none"></div>
              <div className="w-full flex flex-col items-center justify-center z-20 mt-2">
                <ClockDisplay />
                <div className="w-10 h-[1px] bg-[#C79F4B]/40 mt-6"></div>
              </div>
              <div className="flex flex-col items-center justify-center z-20 flex-1 my-8">
                <img src="https://khothi-cms.buso.asia/images/logo-v.png" alt="Logo" className="h-24 md:h-28 object-contain drop-shadow-2xl mb-8 animate-float rounded-[5px]" />
                <h2 className="text-white/95 text-lg md:text-xl font-light tracking-[0.2em] uppercase text-center drop-shadow-md">{t('checkin.idle.greeting')}</h2>
                <p className="text-[#FDE08B]/80 text-sm font-light mt-3 italic tracking-wide">{t('checkin.idle.tagline')}</p>
                {/* Để nhân viên thấy ngay máy đang đặt lịch vào cơ sở nào */}
                {kioskBranch && (
                  <p className="flex items-center gap-1.5 text-white/60 text-xs mt-4 tracking-wide"><MapPin size={12} />{kioskBranch.name}</p>
                )}
              </div>
              <div className="flex flex-col items-center justify-center z-20 mb-2">
                <div className="relative flex items-center justify-center w-24 h-24 mb-6">
                  <div className="absolute w-full h-full border-[1.5px] border-[#C79F4B]/40 rounded-full animate-ripple"></div>
                  <div className="absolute w-full h-full border-[1.5px] border-[#C79F4B]/20 rounded-full animate-ripple-delayed"></div>
                  <div className="w-16 h-16 bg-white/5 backdrop-blur-sm border border-[#C79F4B]/50 rounded-full flex items-center justify-center text-[#FDE08B] transition-all hover:bg-[#C79F4B]/20 hover:scale-105 shadow-[0_0_15px_rgba(199,159,75,0.15)]">
                    <Pointer size={32} strokeWidth={1.5} />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="h-[1px] w-10 md:w-12 bg-gradient-to-r from-transparent to-[#C79F4B]/60"></div>
                  <span className="text-xs md:text-sm text-white/90 font-light tracking-[0.15em] uppercase">{t('checkin.idle.tap')}</span>
                  <div className="h-[1px] w-10 md:w-12 bg-gradient-to-l from-transparent to-[#C79F4B]/60"></div>
                </div>
              </div>
            </div>
          )}

          {/* MÀN HÌNH: NHẬP SĐT */}
          {clientScreen === 'welcome' && (
            <div className="bg-white/95 backdrop-blur-md p-6 md:p-8 fade-in min-h-[450px] kiosk-short-pad">
              <div className="text-center mb-6 kiosk-short-mb">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-100 rounded-full mb-3 text-spa-dark kiosk-short-hide">
                  <BadgeCheck size={24} />
                </div>
                <h2 className="text-gray-800 text-lg font-semibold">{t('checkin.welcome.title')}</h2>
                <p className="text-gray-500 text-sm mt-1">{t('checkin.welcome.subtitle')}</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">{t('checkin.phone')}</label>
                  <div className="relative">
                    <CountryCodePicker value={dialCode} onChange={setDialCode} />
                    {/* Chỉ hiển thị: nhập bằng bàn số bên dưới, chạm vào ô không mở bàn phím của máy */}
                    <input type="tel" inputMode="none" readOnly tabIndex={-1} autoComplete="off" value={isVnPhone ? groupPhone(phone) : phone}
                      placeholder={t('checkin.phone_placeholder')} className="w-full pl-[104px] pr-4 py-3 md:py-4 border border-gray-200 rounded-xl text-lg md:text-2xl text-center font-bold tracking-[0.2em] placeholder:tracking-normal placeholder:font-medium placeholder:text-base pointer-events-none select-none focus:outline-none bg-gray-50 text-spa-dark shadow-inner" />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-2 md:gap-3 py-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                    <button key={num} onClick={() => appendNumber(num.toString())} className="h-12 md:h-16 kiosk-short-key bg-white border border-gray-100 rounded-xl text-xl md:text-2xl font-medium text-gray-700 shadow-sm hover:bg-gray-50 active:bg-gray-200 active:scale-95 transition-all">{num}</button>
                  ))}
                  <button onClick={deleteNumber} aria-label={t('checkin.keypad.delete')} className="h-12 md:h-16 kiosk-short-key bg-red-50 border border-red-100 rounded-xl text-xl font-medium text-red-500 shadow-sm hover:bg-red-100 active:bg-red-200 active:scale-95 transition-all flex items-center justify-center">
                    <Delete size={24} />
                  </button>
                  <button onClick={() => appendNumber('0')} className="h-12 md:h-16 kiosk-short-key bg-white border border-gray-100 rounded-xl text-xl md:text-2xl font-medium text-gray-700 shadow-sm hover:bg-gray-50 active:bg-gray-200 active:scale-95 transition-all">0</button>
                  <button onClick={() => processCheckin()} disabled={checking} aria-label={t('checkin.keypad.submit')} className="h-12 md:h-16 kiosk-short-key bg-spa-dark border border-spa-dark rounded-xl text-white shadow-md hover:bg-slate-800 active:scale-95 transition-all flex items-center justify-center disabled:opacity-70 disabled:cursor-wait">
                    {checking ? <Loader2 size={26} className="animate-spin" /> : <ArrowRight size={28} />}
                  </button>
                </div>

                <div className="flex flex-col pt-1">
                  <div className="relative flex items-center py-2">
                    <div className="flex-grow border-t border-gray-200"></div>
                    <span className="flex-shrink-0 mx-4 text-gray-400 text-xs uppercase">{t('checkin.or')}</span>
                    <div className="flex-grow border-t border-gray-200"></div>
                  </div>
                  <button onClick={() => setClientScreen('scanner')} className="w-full bg-white text-spa-dark border border-gray-200 py-3 rounded-xl text-sm font-medium hover:bg-gray-50 transition-all flex items-center justify-center gap-2 mt-2">
                    <QrCode size={20} /> {t('checkin.scan_qr_button')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* MÀN HÌNH: QUÉT QR */}
          {clientScreen === 'scanner' && (
            <div className="bg-white fade-in flex-1 min-h-0 flex flex-col">
              <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6">
                <div className="max-w-xl mx-auto min-h-full flex flex-col">
                  <div className="text-center mb-6">
                    <h2 className="text-gray-800 text-lg font-semibold">{t('checkin.scanner.title')}</h2>
                    <p className="text-gray-500 text-sm mt-1">{t('checkin.scanner.subtitle')}</p>
                  </div>
                  <div className="flex-1 flex flex-col justify-center">
                    <div className="relative">
                      <QrScanner key={scanKey} onResult={handleQrResult} />
                      {checking && (
                        <div className="absolute inset-0 mb-4 rounded-xl bg-black/60 flex flex-col items-center justify-center gap-3 text-white z-20">
                          <Loader2 size={32} className="animate-spin" />
                          <span className="text-sm">{t('checkin.scanner.checking')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="shrink-0 px-5 md:px-6 py-4 bg-white border-t border-gray-100">
                <div className="max-w-3xl mx-auto">
                  <button onClick={() => setClientScreen('welcome')} className="w-full bg-gray-100 text-gray-700 py-3.5 rounded-xl text-sm md:text-base font-medium hover:bg-gray-200 transition-all flex items-center justify-center gap-2">
                    <XCircle size={20} /> {t('checkin.scanner.cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* MÀN HÌNH: ĐĂNG KÝ (khách chưa có lịch / khách mới) */}
          {clientScreen === 'register' && (
            // Nội dung cuộn ở giữa, hai nút thao tác luôn nằm cố định ở chân màn
            <div className="bg-white fade-in flex-1 min-h-0 flex flex-col">
              <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6">
                <div className="max-w-3xl mx-auto">
                <div className="text-center mb-6">
                  <h2 className="text-gray-800 text-xl font-semibold">{t('checkin.register.title')}</h2>
                  <p className="text-gray-500 text-sm mt-1">{t(regCustomer.id ? 'checkin.register.subtitle' : 'checkin.register.subtitle_new')}</p>                </div>

                {regCustomer.loading ? (
                  <div className="py-10 flex items-center justify-center gap-2 text-sm text-gray-400">
                    <Loader2 size={16} className="animate-spin" /> {t('checkin.register.loading_profile')}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{t('checkin.phone')}</label>
                        <input type="text" readOnly value={regLocked ? maskPhone(regData.phone) : regData.phone} className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm text-gray-800 bg-gray-50 cursor-not-allowed focus:outline-none" />
                      </div>
                      {/* Không cho khách chọn chi nhánh: lịch hẹn gửi cơ sở đã cài cho máy (xem submitRegistration) */}
                      <div id="reg-name">
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{t('checkin.register.name')} <span className="text-red-500">*</span></label>
                        <input type="text" value={regLocked ? maskName(regData.name) : regData.name} readOnly={regLocked} onChange={(e) => setRegData({ ...regData, name: e.target.value })} placeholder={t('checkin.register.name_placeholder')}
                          className={`w-full h-10 border rounded-lg px-3 text-sm text-gray-800 focus:outline-none ${hasErr('name') ? 'border-red-400' : 'border-gray-300'} ${regLocked ? 'bg-gray-50 cursor-not-allowed' : 'bg-white focus:border-spa-dark'}`} />
                        {errText('name', 'checkin.toast.name_required')}
                        {regLocked && <p className="text-[11px] text-gray-400 mt-1">{t('checkin.register.profile_hint')}</p>}
                      </div>
                    </div>

                    {/* Khách mới: hỏi thêm giới tính & ngày sinh (không bắt buộc) để tạo hồ sơ */}
                    {!regCustomer.id && (
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{t('checkin.register.gender')}</label>
                          <div className="flex gap-1.5">
                            {[{ v: 1, k: 'male' }, { v: 2, k: 'female' }, { v: 3, k: 'other_gender' }].map(g => (
                              <button key={g.v} type="button" onClick={() => setRegData({ ...regData, gender: regData.gender === g.v ? null : g.v })}
                                className={`flex-1 h-10 rounded-lg border text-sm font-medium transition-colors ${regData.gender === g.v ? 'bg-spa-dark border-spa-dark text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                {t(`checkin.register.${g.k}`)}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{t('checkin.register.dob')}</label>
                          <DobPicker value={regData.dob} onChange={(dob) => setRegData(prev => ({ ...prev, dob }))} />
                        </div>
                      </div>
                    )}

                    {/* Địa chỉ: bắt buộc như trang Tạo lịch hẹn; khách nước ngoài chỉ cần địa chỉ chi tiết */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <label className="block text-sm font-semibold text-[#2f855a]">{t('checkin.register.address_section')}</label>
                        {/* Người nước ngoài: tỉnh / thành và phường / xã không bắt buộc */}
                        <button type="button" role="checkbox" aria-checked={regForeign}
                          onClick={() => setRegData(prev => ({ ...prev, isForeign: !regForeign }))}
                          className={`flex items-center gap-2 h-10 px-3 rounded-lg border text-sm font-medium transition-colors select-none ${regForeign ? 'bg-green-50 border-[#359766] text-spa-dark' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                          <span className={`w-5 h-5 shrink-0 rounded border flex items-center justify-center transition-colors ${regForeign ? 'bg-spa-dark border-spa-dark' : 'bg-white border-gray-300'}`}>
                            {regForeign && <Check size={12} strokeWidth={3} className="text-white" />}
                          </span>
                          {t('checkin.register.foreign')}
                        </button>
                      </div>
                      {profileAddressOk && (
                        <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-3 flex items-center gap-2">
                          <MapPin size={14} className="text-gray-400 shrink-0" />{t('checkin.register.address_on_file')}
                        </p>
                      )}
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div id="reg-province">
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{t('checkin.register.province')} {!regForeign && !profileAddressOk && <span className="text-red-500">*</span>}</label>
                          <Select className="kiosk-select w-full" size="large" showSearch allowClear status={hasErr('province') ? 'error' : undefined}
                            value={regData.provinceCode || undefined} onChange={handleProvinceChange}
                            options={provinceOptions} filterOption={filterOptionVietnamese}
                            placeholder={t('checkin.register.select_province')} />
                          {errText('province', 'checkin.register.required_select')}
                        </div>
                        <div id="reg-ward">
                          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{t('checkin.register.ward')} {!regForeign && !profileAddressOk && <span className="text-red-500">*</span>}</label>
                          <Select className="kiosk-select w-full" size="large" showSearch allowClear status={hasErr('ward') ? 'error' : undefined}
                            value={regData.wardCode || undefined} onChange={(code) => setRegData({ ...regData, wardCode: code || '' })}
                            options={wardOptions} filterOption={filterOptionVietnamese} loading={loadingWards}
                            disabled={!regData.provinceCode} placeholder={t('checkin.register.select_ward')} />
                          {errText('ward', 'checkin.register.required_select')}
                        </div>
                      </div>
                      <div id="reg-address">
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{t('checkin.register.address')} {!profileAddressOk && <span className="text-red-500">*</span>}</label>
                        <input type="text" value={regData.address} onChange={(e) => setRegData({ ...regData, address: e.target.value })} placeholder={t('checkin.register.address_placeholder')}
                          className={`w-full h-10 border rounded-lg px-3 text-sm text-gray-800 bg-white focus:outline-none focus:border-spa-dark ${hasErr('address') ? 'border-red-400' : 'border-gray-300'}`} />
                        {errText('address', 'checkin.register.required_input')}
                      </div>
                    </div>
                  </>
                )}

                {/* Chọn ngày & giờ đến */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-[#2f855a] mb-3">{t('checkin.register.choose_date')}</label>
                  <div className="flex gap-2 mb-5">
                    {(['today', 'tomorrow'] as const).map(mode => (
                      <button key={mode} type="button" onClick={() => setRegDate(mode)}
                        className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-colors ${regData.dateMode === mode ? 'bg-[#359766] border-[#359766] text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                        {t(`checkin.register.${mode}`)}
                      </button>
                    ))}
                    <div className="flex-1 relative">
                      <button type="button" onClick={() => setVisitPadOpen(true)}
                        className={`w-full py-2.5 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-2 ${regData.dateMode === 'other' ? 'bg-[#359766] border-[#359766] text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                        <CalendarDays size={16} />
                        <span>{regData.dateMode === 'other' ? toDm(regDay) : t('checkin.register.other_date')}</span>
                      </button>
                    </div>
                    {/* Bàn số chọn ngày đến, thay lịch của trình duyệt cho dễ bấm trên máy cảm ứng */}
                    {visitPadOpen && (
                      <DatePadDialog
                        title={t('checkin.visit_date.title')}
                        hint={t('checkin.visit_date.hint')}
                        mask={t('checkin.visit_date.mask')}
                        initialDigits={regData.dateMode === 'other' && regData.customDate ? regData.customDate.slice(8, 10) + regData.customDate.slice(5, 7) : ''}
                        parse={parseVisitDate}
                        describe={(ymd) => new Date(`${ymd}T00:00:00`).toLocaleDateString(isVi ? 'vi-VN' : 'en-GB', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
                        invalidText={t('checkin.visit_date.invalid')}
                        onDone={pickVisitDate}
                        onClose={() => setVisitPadOpen(false)}
                      />
                    )}
                  </div>

                  <label id="reg-time" className="block text-xs font-medium text-center text-gray-500 mb-3">{t('checkin.register.arrival_time')}</label>
                  {hasErr('time') && <p className="text-xs text-red-500 text-center -mt-2 mb-2">{t('checkin.toast.time_required')}</p>}
                  {regSlots.length ? (
                    <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 gap-2">
                      {regSlots.map(slot => (
                        <button key={slot} type="button" onClick={() => setRegData({ ...regData, time: slot })}
                          className={`py-1.5 rounded border text-sm transition-colors focus:outline-none ${slot === regData.time ? 'bg-[#359766] border-[#359766] text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-[#359766]'}`}>
                          {slot}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-center text-gray-400 italic">{t('checkin.register.no_slots')}</p>
                  )}
                </div>
                <hr className="mb-6 border-gray-200" />

                {/* Tìm & lọc dịch vụ */}
                <div className="mb-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <label className="block text-xs font-semibold text-gray-500 uppercase pt-1">
                      {t('checkin.register.services')} <span className="text-gray-400 font-normal text-[11px] normal-case">{t('checkin.register.multi')}</span>
                    </label>
                    <div className="text-right shrink-0">
                      <span className="text-xs font-medium text-spa-dark bg-green-50 px-2 py-1 rounded-md">{t('checkin.register.selected_count', { count: regData.selectedServices.length })}</span>
                      {regData.selectedServices.length > 0 && (
                        <div className="text-xs font-bold text-spa-dark mt-1">
                          {t('checkin.register.total', { total: selectedTotal === 0 ? t('checkin.free') : formatVnd(selectedTotal) })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="relative mb-3">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400"><Search size={16} /></span>
                    <input type="text" value={serviceSearch} onChange={(e) => setServiceSearch(e.target.value)} placeholder={t('checkin.register.search_placeholder')}
                      className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-spa-dark" />
                  </div>

                  {/* Nhóm dịch vụ xuống dòng, không cắt chữ (tên nhóm khá dài) */}
                  {serviceCategories.length > 1 && (
                    <div className="flex flex-wrap gap-2 pb-2 mb-2">
                      {[{ id: 'all' as const, name: t('checkin.register.all') }, ...serviceCategories].map(cat => (
                        <button key={cat.id} type="button" onClick={() => setServiceCategory(cat.id)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border text-left transition-colors ${serviceCategory === cat.id ? 'bg-spa-dark border-spa-dark text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  )}
                  {hasErr('services') && <p className="text-xs text-red-500 mb-2">{t('checkin.toast.service_required')}</p>}
                </div>

                {/* Danh sách dịch vụ: cuộn cùng trang, không lồng thêm một khung cuộn riêng (khó vuốt trên iPad) */}
                <div id="reg-services" className={`border rounded-lg bg-white ${hasErr('services') ? 'border-red-400' : 'border-gray-200'}`}>
                  {kioskServices.status !== 'ready' ? (
                    <div className="p-6 flex flex-col items-center gap-2 text-sm text-gray-400">
                      {kioskServices.status === 'error' ? (
                        <>
                          <span>{t('checkin.register.no_services')}</span>
                          <button type="button" onClick={loadKioskServices} className="flex items-center gap-1 text-spa-dark font-medium"><RefreshCw size={14} /> {t('checkin.admin.refresh')}</button>
                        </>
                      ) : (
                        <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> {t('checkin.register.loading_services')}</span>
                      )}
                    </div>
                  ) : visibleServices.length === 0 ? (
                    <div className="p-6 text-center text-sm text-gray-400">{t('checkin.register.no_services')}</div>
                  ) : visibleServices.map(svc => {
                    const isSelected = regData.selectedServices.includes(svc.id);
                    return (
                      <button key={svc.id} type="button"
                        onClick={() => setRegData(prev => ({
                          ...prev,
                          selectedServices: isSelected ? prev.selectedServices.filter(id => id !== svc.id) : [...prev.selectedServices, svc.id],
                        }))}
                        className="w-full flex items-center justify-between gap-3 p-3 border-b border-gray-100 last:border-b-0 text-left hover:bg-gray-50 transition-colors select-none">
                        <span className="flex items-center gap-3 min-w-0">
                          <span className={`w-5 h-5 shrink-0 rounded border flex items-center justify-center transition-colors ${isSelected ? 'bg-spa-dark border-spa-dark' : 'bg-white border-gray-300'}`}>
                            {isSelected && <Check size={12} strokeWidth={3} className="text-white" />}
                          </span>
                          <span className="text-sm text-gray-700 font-medium">{svc.name}</span>
                        </span>
                        <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{svc.price === 0 ? t('checkin.free') : svc.priceText}</span>
                      </button>
                    );
                  })}
                </div>
                </div>
              </div>

              <div className="shrink-0 px-5 md:px-6 py-4 border-t border-gray-200 bg-white">
                <div className="max-w-3xl mx-auto flex gap-3">
                  <button onClick={() => setClientScreen('welcome')} disabled={!!submitStep} className="w-1/3 py-3.5 rounded-xl border border-gray-300 text-gray-700 text-sm md:text-base font-medium hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed">{t('checkin.register.back')}</button>
                  <button onClick={submitRegistration} disabled={!!submitStep || regCustomer.loading}
                    className="w-2/3 py-3.5 rounded-xl bg-spa-dark text-white text-sm md:text-base font-medium hover:bg-slate-800 transition shadow-lg flex items-center justify-center gap-2 disabled:opacity-80 disabled:cursor-wait">
                    {submitStep ? <><Loader2 size={16} className="animate-spin" /> {t(`checkin.register.step_${submitStep}`)}</>
                      // Hẹn ngày khác chỉ đặt lịch, không check-in (xem submitRegistration)
                      : regIsToday ? t('checkin.register.submit') : t('checkin.register.submit_booking', { date: toDm(regDay) })}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/*
            MÀN HÌNH: XÁC NHẬN — nhân viên đọc lại thông tin với khách rồi mới check-in thật
            MÀN HÌNH: ĐÃ CHECK-IN — lịch đã check-in trước đó, hiển thị đầy đủ thông tin lịch hẹn
          */}
          {(clientScreen === 'confirm' || clientScreen === 'already') && apptView && (() => {
            const already = clientScreen === 'already';
            const info = successFromAppointment(apptView.appointment, apptView.fallback);
            // Lịch ngày đã qua: chỉ báo lịch, không cho check-in. Lịch ngày sau (khách đến sớm): vẫn check-in, lịch chuyển sang hôm nay
            const scheduledAt: string = apptView.appointment?.scheduled_at || '';
            const early = !already && isFutureDay(scheduledAt);
            const otherDay = !already && !early && isOtherDay(scheduledAt);
            const [, apptMonth, apptDay] = scheduledAt.slice(0, 10).split('-');
            const tagChip = (tag: CheckinTag | null) => tag && (
              <span className="inline-flex items-center gap-1.5 font-semibold text-gray-800">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: tag.color || '#94a3b8' }} />{tag.name}
              </span>
            );
            return (
              <div className="bg-white fade-in flex-1 min-h-0 flex flex-col">
                <div className="flex-1 overflow-y-auto px-6 md:px-8 pt-7 pb-4">
                  <div className="max-w-3xl mx-auto">
                  {already ? (
                    <div className="text-center mb-5">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border-2 border-emerald-500 bg-emerald-50 text-emerald-500 mb-3">
                        <BadgeCheck size={28} />
                      </div>
                      <h2 className="text-gray-800 text-xl font-bold">{t('checkin.dialog.already_title')}</h2>
                      <p className="text-emerald-700 text-sm mt-4 bg-emerald-50 px-4 py-3 rounded-xl border border-emerald-100 leading-relaxed">
                        {t('checkin.dialog.already_desc', { code: info.code || '' })}
                      </p>
                    </div>
                  ) : otherDay ? (
                    <div className="text-center mb-5">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border-2 border-red-300 bg-red-50 text-red-500 mb-3">
                        <CalendarClock size={26} />
                      </div>
                      <h2 className="text-gray-800 text-xl font-bold">{t('checkin.confirm.other_day_title')}</h2>
                      <p className="text-red-700 text-base font-semibold mt-4 bg-red-50 px-4 py-3 rounded-xl border border-red-200 leading-relaxed">
                        {t('checkin.confirm.other_day_desc', { time: scheduledAt.slice(11, 16), date: `${apptDay}/${apptMonth}` })}
                      </p>
                      <p className="text-gray-500 text-sm mt-2">{t('checkin.confirm.other_day_hint')}</p>
                    </div>
                  ) : (
                    <div className="text-center mb-5">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border-2 border-amber-300 bg-amber-50 text-spa-gold mb-3">
                        <ClipboardList size={26} />
                      </div>
                      <h2 className="text-gray-800 text-xl font-bold">{t('checkin.confirm.title')}</h2>
                      <p className="text-amber-700 text-sm mt-4 bg-amber-50 px-4 py-3 rounded-xl border border-amber-100 leading-relaxed">{t('checkin.confirm.hint')}</p>
                      {early && (
                        <p className="text-sky-800 text-sm font-semibold mt-2 bg-sky-50 px-4 py-3 rounded-xl border border-sky-200 leading-relaxed">
                          {t('checkin.confirm.early_day', { time: scheduledAt.slice(11, 16), date: `${apptDay}/${apptMonth}` })}
                        </p>
                      )}
                      {/* Chỉ cảnh báo ở máy đã cài cơ sở (?branch=): máy chưa cài không biết mình đang ở đâu */}
                      {kioskBranchId != null && apptView.appointment.branch_id != null && Number(apptView.appointment.branch_id) !== Number(kioskBranchId) && (
                        <p className="text-red-700 text-sm font-semibold mt-2 bg-red-50 px-4 py-3 rounded-xl border border-red-200 leading-relaxed">
                          {t('checkin.confirm.other_branch', { branch: info.branch || '' })}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Thông tin cần đối chiếu với khách: họ tên, SĐT đầy đủ */}
                    <div className="bg-gray-50 rounded-xl border border-gray-200 px-4 py-4">
                      <div className="flex items-center gap-2 mb-3">
                        <User className="text-gray-500" size={16} />
                        <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wide">{t('checkin.success.customer_info')}</h3>
                      </div>
                      <div className="text-xl font-bold text-spa-dark leading-snug">{maskName(info.name)}</div>
                      <div className="text-lg font-semibold text-gray-700 tracking-wider mt-1">{maskPhone(info.phone) || '—'}</div>
                    </div>

                    <div className="bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 text-sm space-y-2">
                      {info.scheduled && (
                        <div className="flex justify-between items-center gap-3">
                          <span className="flex items-center gap-2 text-gray-500"><CalendarClock className="text-gray-400" size={16} />{t('checkin.success.scheduled')}:</span>
                          <span className="flex items-center gap-2">
                            {!already && lateBadge(scheduledAt)}
                            <span className="font-bold text-gray-800">{info.scheduled}</span>
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center gap-3">
                        <span className="flex items-center gap-2 text-gray-500 shrink-0"><MapPin className="text-gray-400" size={16} />{t('checkin.success.branch')}</span>
                        <span className="font-bold text-gray-800 text-right">{info.branch || '—'}</span>
                      </div>
                      {info.code && (
                        <div className="flex justify-between items-center gap-3">
                          <span className="flex items-center gap-2 text-gray-500"><Hash className="text-gray-400" size={16} />{t('checkin.success.code')}:</span>
                          <span className="font-mono font-bold text-gray-800">{info.code}</span>
                        </div>
                      )}
                      {/* Thẻ S là mã nội bộ: chỉ hiện khi lễ tân mở từ màn Lễ tân, khách ở kiosk không cần thấy */}
                      {apptView.from === 'admin' && (apptView.currentTag || apptView.targetTag) && (
                        <div className="flex justify-between items-start gap-3 border-t border-gray-200 pt-2">
                          <span className="flex items-center gap-2 text-gray-500 shrink-0"><ArrowRightLeft className="text-gray-400" size={16} />{t('checkin.confirm.status')}</span>
                          <span className="flex flex-col items-end gap-0.5 text-right text-[13px]">
                            {tagChip(apptView.currentTag)}
                            {apptView.targetTag && !otherDay && <span className="flex items-center gap-1.5 text-gray-400"><ArrowRight size={13} />{tagChip(apptView.targetTag)}</span>}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="bg-white rounded-xl border border-gray-200 px-4 py-4 text-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <ListChecks className="text-gray-500" size={16} />
                        <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wide">{t('checkin.success.service_detail')}</h3>
                      </div>
                      {/* Kiosk đặt ở sảnh nên không hiện giá; chuyên viên tư vấn báo giá riêng với khách */}
                      <div className="space-y-2.5">
                        {info.services?.length ? info.services.map((svc, i) => (
                          <div key={i} className="font-medium text-gray-800">{svc.name}</div>
                        )) : <div className="text-gray-500 italic">{t('checkin.success.service_tbd')}</div>}
                      </div>
                    </div>
                  </div>
                  </div>
                </div>

                <div className="shrink-0 px-5 md:px-6 py-4 bg-white border-t border-gray-100">
                  {already ? (
                    <div className="max-w-3xl mx-auto">
                      <button onClick={() => { setApptView(null); setPhone(''); setClientScreen('idle'); }}
                        className="w-full py-3.5 rounded-xl bg-spa-dark text-white font-semibold text-sm md:text-base hover:bg-slate-800 active:scale-[0.99] transition-all shadow-md">
                        {t('checkin.dialog.already_ok')}
                      </button>
                    </div>
                  ) : otherDay ? (
                    <div className="max-w-3xl mx-auto">
                      <button onClick={rejectPendingCheckin}
                        className="w-full py-3.5 rounded-xl bg-spa-dark text-white font-semibold text-sm md:text-base hover:bg-slate-800 active:scale-[0.99] transition-all shadow-md flex items-center justify-center gap-2">
                        <ChevronLeft size={18} />{t('checkin.nav.back')}
                      </button>
                    </div>
                  ) : (
                    <div className="max-w-3xl mx-auto flex gap-3">
                      <button onClick={rejectPendingCheckin} disabled={checking}
                        className="w-2/5 py-3.5 rounded-xl bg-gray-100 text-gray-700 font-medium text-sm md:text-base hover:bg-gray-200 transition-colors disabled:opacity-50">
                        {t('checkin.confirm.wrong')}
                      </button>
                      <button onClick={confirmPendingCheckin} disabled={checking}
                        className="flex-1 py-3.5 rounded-xl bg-spa-dark text-white font-semibold text-sm md:text-base hover:bg-slate-800 active:scale-[0.99] transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-wait">
                        {checking ? <Loader2 size={18} className="animate-spin" /> : <><CheckCircle2 size={18} />{t('checkin.confirm.submit')}</>}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* MÀN HÌNH: THÀNH CÔNG */}
          {clientScreen === 'success' && successData && (
            // Nội dung cuộn ở giữa, nút "Về trang chủ" luôn nằm cố định ở chân màn
            <div className="bg-white fade-in flex-1 min-h-0 flex flex-col">
              <div className="flex-1 overflow-y-auto px-6 md:px-8 pt-8 pb-4">
                <div className="max-w-3xl mx-auto">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border-2 border-emerald-500 bg-emerald-50 text-emerald-500 mb-3 scale-in">
                    <Check size={28} strokeWidth={2.5} />
                  </div>
                  <h2 className="text-gray-800 text-xl font-bold">{t(successData.mode === 'booked' ? 'checkin.success.booked_title' : 'checkin.success.title')}</h2>
                  <p className="text-emerald-600 text-sm mt-5 bg-emerald-50 px-4 py-3 rounded-xl border border-emerald-100 leading-relaxed">
                    {successData.mode === 'booked' ? t('checkin.success.booked_thanks', { time: successData.scheduled }) : t('checkin.success.thanks')}
                  </p>
                </div>

                <div className="space-y-5">
                  {/* Thời gian & cơ sở; chỉ đặt lịch (chưa check-in) thì không có ngày / giờ check-in */}
                  <div className="bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 text-sm">
                    <div className="space-y-2">
                      {successData.mode !== 'booked' && (
                        <>
                          <div className="flex justify-between items-center gap-3">
                            <span className="flex items-center gap-2 text-gray-500"><CalendarDays className="text-gray-400" size={16} />{t('checkin.success.checkin_date')}</span>
                            <span className="font-bold text-gray-800">{successData.date.toLocaleDateString(isVi ? 'vi-VN' : 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                          </div>
                          <div className="flex justify-between items-center gap-3">
                            <span className="flex items-center gap-2 text-gray-500"><Clock className="text-gray-400" size={16} />{t('checkin.success.checkin_time')}</span>
                            <span className="font-bold text-gray-800">{successData.time}</span>
                          </div>
                        </>
                      )}
                      {successData.scheduled && (
                        <div className="flex justify-between items-center gap-3">
                          <span className="flex items-center gap-2 text-gray-500"><CalendarClock className="text-gray-400" size={16} />{t('checkin.success.scheduled')}:</span>
                          <span className="flex items-center gap-2">
                            {successData.mode !== 'booked' && lateBadge(successData.scheduledAt)}
                            <span className="font-bold text-gray-800">{successData.scheduled}</span>
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="border-t border-gray-200 mt-3 pt-3 space-y-2">
                      <div className="flex justify-between items-center gap-3">
                        <span className="flex items-center gap-2 text-gray-500 shrink-0"><MapPin className="text-gray-400" size={16} />{t('checkin.success.branch')}</span>
                        <span className="font-bold text-gray-800 text-right">{successData.branch || '—'}</span>
                      </div>
                      {successData.code && (
                        <div className="flex justify-between items-center gap-3">
                          <span className="flex items-center gap-2 text-gray-500"><Hash className="text-gray-400" size={16} />{t('checkin.success.code')}:</span>
                          <span className="font-mono font-bold text-gray-800">{successData.code}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Thông tin khách hàng */}
                  <div className="bg-gray-50 rounded-xl border border-gray-200 px-4 py-4 text-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <User className="text-gray-500" size={16} />
                      <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wide">{t('checkin.success.customer_info')}</h3>
                    </div>
                    <div className="space-y-2.5">
                      <div className="flex justify-between gap-3"><span className="text-gray-500">{t('checkin.success.name')}</span> <span className="font-medium text-gray-800 text-right">{maskName(successData.name)}</span></div>
                      <div className="flex justify-between gap-3"><span className="text-gray-500">{t('checkin.success.phone')}</span> <span className="font-medium text-gray-800">{maskPhone(successData.phone)}</span></div>
                    </div>
                  </div>

                  {/* Thẻ liệu trình: không hiện tên gói ở sảnh, ai nhập số này cũng đọc được; Lễ tân chọn thẻ cùng khách */}
                  {(successData.treatment?.need_choice || successData.treatment?.no_session) && (
                    <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
                      {t(successData.treatment.no_session ? 'checkin.success.card_no_session' : 'checkin.success.card_need_choice')}
                    </div>
                  )}

                  {/* Chi tiết dịch vụ */}
                  <div className="bg-white rounded-xl border border-gray-200 px-4 py-4 text-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <ListChecks className="text-gray-500" size={16} />
                      <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wide">{t('checkin.success.service_detail')}</h3>
                    </div>
                    {/* Không hiện giá trên kiosk (xem màn xác nhận) */}
                    <div className="space-y-3">
                      {successData.services?.length ? successData.services.map((svc, i) => (
                        <div key={i}>
                          <div className="font-medium text-gray-800">{svc.name}</div>
                          {svc.note && <div className="text-xs text-gray-400 mt-0.5">{svc.note}</div>}
                        </div>
                      )) : (
                        <div className="text-gray-500 italic">{t('checkin.success.service_tbd')}</div>
                      )}
                    </div>
                  </div>
                </div>
                </div>
              </div>

              <div className="shrink-0 px-5 md:px-6 py-4 bg-white border-t border-gray-100">
                <div className="max-w-3xl mx-auto">
                  {successData.mode !== 'booked' && successData.apptId ? (
                    <button onClick={openIntake} className="w-full bg-[#092C23] text-white py-3.5 rounded-xl text-sm md:text-base font-semibold hover:bg-[#0d3a2f] active:scale-[0.99] transition-all">
                      {isVi ? 'Tiếp tục: khai báo thông tin' : 'Continue: fill in your information'} <span className="text-white/60 tabular-nums">({successLeft}s)</span>
                    </button>
                  ) : (
                    <button onClick={() => { setPhone(''); setClientScreen('idle'); }} className="w-full bg-gray-50 border border-gray-200 text-gray-700 py-3.5 rounded-xl text-sm md:text-base font-medium hover:bg-gray-100 active:scale-[0.99] transition-all">
                      {t('checkin.success.home')} <span className="text-gray-400 tabular-nums">({successLeft}s)</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Hộp thoại theo kết quả check-in: đã check-in / gợi ý lịch mới / chưa có lịch / khách mới */}
          {dialog && (() => {
            const view = dialogView(dialog);
            return (
              <div className="fixed inset-0 z-[60] flex items-center justify-center fade-in">
                <div className="absolute inset-0 bg-[#092C23]/70 backdrop-blur-sm" onClick={view.cancel ? closeDialog : undefined}></div>
                <div className="bg-white rounded-[24px] shadow-2xl w-11/12 max-w-sm md:max-w-lg relative z-10 p-6 md:p-8 text-center border border-gray-100">
                  <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-5 shadow-sm border ${view.tone}`}>
                    {view.icon}
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2 md:mb-3">{view.title}</h3>
                  <p className="text-gray-500 text-sm md:text-base mb-6 md:mb-8 leading-relaxed px-1 md:px-4">{view.desc}</p>
                  <div className="flex flex-col-reverse sm:flex-row gap-3">
                    {view.cancel && (
                      <button onClick={closeDialog} disabled={checking} className="w-full sm:w-1/2 py-3 md:py-3.5 rounded-xl bg-gray-100 text-gray-700 font-medium text-sm md:text-base hover:bg-gray-200 transition-colors disabled:opacity-50">{view.cancel}</button>
                    )}
                    <button onClick={view.onConfirm} disabled={checking}
                      className={`w-full ${view.cancel ? 'sm:w-1/2' : ''} py-3 md:py-3.5 rounded-xl bg-spa-dark text-white font-medium text-sm md:text-base hover:bg-slate-800 transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-wait`}>
                      {checking ? <Loader2 size={18} className="animate-spin" /> : <>{view.confirm} {view.cancel && <ArrowRight size={18} />}</>}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  /** Màn Lễ tân: lịch hẹn hôm nay lấy từ API, lọc theo cơ sở / trạng thái, check-in ngay trên bảng */
  const renderAdminView = () => {
    const items = reception.items;
    const counts = RECEPTION_BUCKETS.reduce((acc, b) => ({ ...acc, [b]: items.filter(a => receptionBucket(a) === b).length }), {} as Record<ReceptionBucket, number>);
    const keyword = foldVi(receptionSearch.trim());
    const rows = items.filter(a =>
      (receptionFilter === 'all' || receptionBucket(a) === receptionFilter) &&
      (!keyword || foldVi(`${a.customer_name || a.customer?.fullname || ''} ${a.customer_phone || ''} ${a.code || ''}`).includes(keyword))
    );
    const dateOptions: Intl.DateTimeFormatOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const dateLocale = isVi ? 'vi-VN' : 'en-US';
    const staffName = (() => {
      try { return JSON.parse(localStorage.getItem('user') || '{}')?.fullname || ''; } catch { return ''; }
    })();
    const loading = reception.status === 'loading' || reception.status === 'idle';

    const stats: { key: ReceptionBucket | 'all'; label: string; value: number; icon: React.ReactNode; tone: string }[] = [
      { key: 'all', label: t('checkin.admin.total'), value: items.length, icon: <Users size={24} />, tone: 'bg-blue-50 text-blue-600' },
      { key: 'upcoming', label: t('checkin.admin.upcoming'), value: counts.upcoming, icon: <CalendarClock size={24} />, tone: 'bg-sky-50 text-sky-600' },
      { key: 'waiting', label: t('checkin.admin.waiting'), value: counts.waiting, icon: <Hourglass size={24} />, tone: 'bg-orange-50 text-orange-600' },
      { key: 'served', label: t('checkin.admin.served'), value: counts.served, icon: <CheckCircle2 size={24} />, tone: 'bg-emerald-50 text-emerald-600' },
    ];

    return (
      // Vừa khít chiều cao màn hình: chỉ bảng cuộn, trang không cuộn (tránh cuộn 2 lớp)
      <div className="bg-slate-50 w-full h-screen flex flex-col fade-in">
        <div className="shrink-0 bg-spa-dark border-b border-[#C79F4B]/30 px-6 py-3 flex justify-between items-center gap-4 shadow-sm">
          <div className="flex items-center gap-3 text-white min-w-0">
            <img src="https://khothi-cms.buso.asia/images/logo-v.png" alt="Khothi" className="h-8 object-contain drop-shadow-sm rounded-[5px]" />
            <h1 className="text-sm font-bold tracking-widest uppercase border-l border-white/20 pl-3">{t('checkin.admin.dashboard')}</h1>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => { setCurrentView('client'); setClientScreen('idle'); }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/20 text-white/90 text-xs font-medium hover:bg-white/10 transition-colors">
              <ArrowRightLeft size={14} />{t('checkin.switch_to_client')}
            </button>
            <Select
              className="w-56"
              value={receptionBranch ?? 0}
              onChange={(v: number) => changeReceptionBranch(v || null)}
              options={[{ value: 0, label: t('checkin.admin.all_branches') }, ...branches.map(b => ({ value: b.id, label: b.name }))]}
            />
            <div className="text-xs text-gray-300 flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#C79F4B] text-white flex items-center justify-center font-semibold shrink-0"><User size={15} /></div>
              <div className="min-w-0">
                <p className="font-medium text-white truncate max-w-[180px]">{staffName || t('checkin.admin.receptionist')}</p>
                <p className="text-[10px]">{t('checkin.admin.receptionist')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Màn hình quá thấp (bảng không đủ chỗ tối thiểu) thì mới cho phần thân cuộn */}
        <div className="p-6 max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-y-auto">
          <div className="shrink-0 flex justify-between items-end mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800">{t('checkin.admin.shift_overview')}</h2>
              <p className="text-sm text-gray-500 mt-1">{new Date().toLocaleDateString(dateLocale, dateOptions)}</p>
            </div>
            {reception.updatedAt && (
              <span className="text-xs text-gray-400">{t('checkin.admin.updated', { time: reception.updatedAt.toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit', hour12: false }) })}</span>
            )}
          </div>

          {/* Bấm thẻ số liệu để lọc nhanh bảng bên dưới */}
          <div className="shrink-0 grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {stats.map(s => (
              <button
                key={s.key}
                type="button"
                onClick={() => setReceptionFilter(s.key)}
                className={`bg-white p-4 rounded-xl border shadow-sm flex items-center gap-4 text-left transition-colors ${receptionFilter === s.key ? 'border-spa-gold ring-1 ring-[#C79F4B]' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${s.tone}`}>{s.icon}</div>
                <div><div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{s.label}</div><div className="text-2xl font-bold text-gray-800">{loading ? '—' : s.value}</div></div>
              </button>
            ))}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col flex-1 min-h-[320px]">
            <div className="px-5 py-3 border-b border-gray-200 flex flex-wrap gap-3 justify-between items-center bg-gray-50/50">
              <div className="flex items-center gap-2"><List className="text-gray-500" size={18} /><h2 className="text-sm font-semibold text-gray-800">{t('checkin.admin.queue_title')}</h2></div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex rounded-lg border border-gray-200 bg-white p-0.5 text-xs font-medium">
                  {(['all', ...RECEPTION_BUCKETS] as const).map(k => (
                    <button key={k} type="button" onClick={() => setReceptionFilter(k)}
                      className={`px-2.5 py-1 rounded-md transition-colors ${receptionFilter === k ? 'bg-spa-dark text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                      {t(`checkin.admin.${k}`)} <span className="opacity-70">{k === 'all' ? items.length : counts[k]}</span>
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={receptionSearch} onChange={e => setReceptionSearch(e.target.value)} placeholder={t('checkin.admin.search')}
                    className="pl-8 pr-3 py-1.5 w-52 border border-gray-300 rounded-lg text-xs focus:outline-none focus:border-spa-gold" />
                </div>
                <button onClick={loadReception} disabled={reception.status === 'loading'} className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium hover:bg-gray-50 flex items-center gap-1 disabled:opacity-60">
                  <RefreshCw size={14} className={reception.status === 'loading' ? 'animate-spin' : ''} /> {t('checkin.admin.refresh')}
                </button>
              </div>
            </div>

            <div className="overflow-auto flex-1 p-0 relative">
              {loading ? (
                <div className="flex items-center justify-center h-full text-gray-400"><Loader2 size={28} className="animate-spin" /></div>
              ) : reception.status === 'error' && !items.length ? (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-500">
                  <AlertCircle size={36} className="text-red-400" />
                  <p className="text-sm">{t('checkin.admin.load_error')}</p>
                  <button onClick={loadReception} className="px-3 py-1.5 bg-spa-dark text-white rounded-lg text-xs font-medium">{t('checkin.admin.retry')}</button>
                </div>
              ) : !rows.length ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400 h-full">
                  <ClipboardList size={48} className="mb-3 text-gray-300" />
                  <p className="text-sm">{items.length ? t('checkin.admin.empty_filter') : t('checkin.admin.empty_queue')}</p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse whitespace-nowrap">
                  <thead className="sticky top-0 bg-gray-50 z-10 shadow-sm">
                    <tr className="text-xs text-gray-500 uppercase tracking-wider">
                      <th className="px-5 py-3 font-semibold">{t('checkin.admin.col_time')}</th>
                      <th className="px-5 py-3 font-semibold">{t('checkin.admin.col_customer')}</th>
                      <th className="px-5 py-3 font-semibold">{t('checkin.admin.col_phone')}</th>
                      <th className="px-5 py-3 font-semibold">{t('checkin.admin.col_service')}</th>
                      {!receptionBranch && <th className="px-5 py-3 font-semibold">{t('checkin.admin.col_branch')}</th>}
                      <th className="px-5 py-3 font-semibold">{t('checkin.admin.col_status')}</th>
                      <th className="px-5 py-3 font-semibold text-right">{t('checkin.admin.col_action')}</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-gray-600 divide-y divide-gray-100">
                    {rows.map(a => {
                      const bucket = receptionBucket(a);
                      const services = (a.services || []).map((s: any) => s.name).filter(Boolean);
                      const tag = a.customer_tag;
                      return (
                        <tr key={a.id} className={`hover:bg-slate-50 transition-colors ${bucket === 'served' || bucket === 'canceled' ? 'opacity-60 bg-gray-50' : ''}`}>
                          <td className="px-5 py-3 font-semibold text-gray-700">{scheduleLabel(a.scheduled_at) || '—'}</td>
                          <td className="px-5 py-3">
                            <div className="font-bold text-gray-800">{a.customer_name || a.customer?.fullname || t('checkin.guest')}</div>
                            {a.code && <div className="text-[11px] font-mono text-gray-400">{a.code}</div>}
                          </td>
                          <td className="px-5 py-3">{a.customer_phone || a.customer?.phone || '—'}</td>
                          <td className="px-5 py-3 text-gray-600 max-w-[280px] truncate" title={services.join(', ')}>
                            {services[0] || '—'}{services.length > 1 && <span className="text-gray-400"> +{services.length - 1}</span>}
                          </td>
                          {!receptionBranch && <td className="px-5 py-3 text-gray-600">{a.branch?.name || '—'}</td>}
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-bold ${RECEPTION_BADGE[bucket]}`}>
                              {bucket === 'waiting' && <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />}
                              {a.customer_tag_status_text || t(`checkin.admin.${bucket}`)}
                            </span>
                            {tag?.name && (
                              <div className="flex items-center gap-1 mt-1 text-[11px] text-gray-500">
                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: tag.color || '#94a3b8' }} />{tag.name}
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-3 text-right">
                            {bucket === 'upcoming' ? (
                              <button onClick={() => checkinFromReception(a)} disabled={!!receptionBusyId}
                                className="px-3 py-1.5 text-[11px] font-semibold text-white bg-spa-dark rounded hover:bg-slate-800 transition-colors disabled:opacity-60 inline-flex items-center gap-1">
                                {receptionBusyId === a.id && <Loader2 size={12} className="animate-spin" />}{t('checkin.admin.checkin')}
                              </button>
                            ) : bucket === 'waiting' ? (
                              <span className="text-xs text-orange-600 font-medium">{t('checkin.admin.checked_in')}</span>
                            ) : bucket === 'served' ? (
                              <span className="text-xs text-gray-400 italic">{t('checkin.admin.done')}</span>
                            ) : <span className="text-xs text-gray-300">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="font-sans antialiased min-h-screen flex flex-col relative bg-slate-50">
      <CustomStyles />

      {/* Chuyển ngôn ngữ ở màn chờ; các màn khác nằm trong thanh tiêu đề. Màn Lễ tân đã có thanh tiêu đề riêng */}
      {currentView === 'client' && clientScreen === 'idle' && (
        <div className="fixed top-5 right-5 z-[70]">{renderLangSwitch()}</div>
      )}

      {/* Sắp tự về màn chờ: chạm bất kỳ đâu (kể cả nút) là tính lại thời gian chờ */}
      {currentView === 'client' && idleWarn && <IdleWarning />}

      {/* Giữa phía trên, gần chỗ khách đang nhìn hơn góc phải */}
      <div className="fixed top-28 inset-x-0 mx-auto z-[9999] w-[min(92vw,520px)] flex flex-col items-center gap-2 pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.id} role="alert" className={`bg-white border-l-4 shadow-lg px-5 py-3 rounded-lg text-sm md:text-base text-gray-800 flex items-center gap-3 fade-in ${toast.type === 'success' ? 'border-green-500' : 'border-red-500'}`}>
            {toast.type === 'success' ? <CheckCircle2 className="text-green-500" size={20} /> : <AlertCircle className="text-red-500" size={20} />}
            <span>{toast.message}</span>
          </div>
        ))}
      </div>

      {/* Màn Lễ tân có nút quay về trên thanh tiêu đề, nút nổi sẽ che nút Check-in của bảng */}
      {SHOW_RECEPTION_SWITCH && currentView === 'client' && (
        <button onClick={() => setCurrentView('admin')} className="fixed bottom-4 right-4 z-50 bg-white border border-gray-200 shadow-lg rounded-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-all">
          <ArrowRightLeft size={16} />
          <span>{t('checkin.switch_to_admin')}</span>
        </button>
      )}

      {/* ĐÃ SỬA: Gọi trực tiếp dưới dạng hàm để tránh render lại Component */}
      {currentView === 'client' ? renderClientView() : renderAdminView()}
    </div>
  );
}